import React, { useState, useEffect, useRef, useMemo } from 'react';
import { searchForView, viewFromSearch, isNavigation } from './lib/viewUrl';
import { findProfileIssues, ISSUE_LABELS, isPlausibleCreatorName } from './lib/profileChecks';
import { trackedChannels } from './lib/channelPrefs';
import { buildProfiles, suggestCollaborators } from './lib/collabSuggest';
import { Avatar, SocialLinks, VideoThumb, youtubeId as youtubeIdOf } from './components/media';
import CreatorPicker from './components/CreatorPicker';
import { Users, Video, Plus, Trash2, ExternalLink, TrendingUp, Search, ArrowLeft, User, Lock, LogOut, Shield, Edit, Send, CheckCircle, XCircle, ChevronDown, ChevronUp, Award, Trophy, RefreshCw, AlertTriangle } from 'lucide-react';

/*
 * Call the YouTube Data API through our own /api/youtube, which holds the key
 * server-side. See api/youtube.js for why the key cannot live out here.
 *
 * This THROWS on failure. The call sites used to read `data.items` straight off
 * a response nobody had checked, so a quota error, which is still valid JSON,
 * left `items` undefined and read exactly like "this video has no data". The
 * app then carried on silently. Throwing forces each caller to decide what to
 * do, and carries a message worth showing.
 */
async function youtubeApi(resource, params = {}) {
  const qs = new URLSearchParams({ resource });
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') qs.set(k, String(v));
  }
  const res = await fetch(`/api/youtube?${qs.toString()}`);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(data?.error || `YouTube lookup failed (HTTP ${res.status})`);
    err.reason = data?.reason;
    err.status = res.status;
    throw err;
  }
  return data;
}

/** True when the failure is YouTube being unavailable to us rather than the
 *  video being missing, which is the difference worth telling a user about. */
const isYoutubeOutage = (err) =>
  ['quotaExceeded', 'rateLimitExceeded', 'notConfigured', 'badKey', 'network'].includes(
    err?.reason
  );

export default function VideoCollabTracker() {
  const [authors, setAuthors] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Whether the SERVER says this browser has a valid session. It is not read
  // from localStorage any more: that was a value anyone could set in devtools
  // to reveal the admin panel. It now starts false and is confirmed by
  // /api/auth below, which reads an HttpOnly cookie JavaScript cannot forge.
  //
  // Revealing the admin UI was never the real problem, though. The write
  // endpoints themselves now require the same session, so a faked flag here
  // shows someone the buttons and every one of them returns 401.
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginError, setLoginError] = useState('');
  // Shown when YouTube lookups are degraded, usually because the daily quota
  // is spent. Previously this state was invisible: the app just quietly
  // stopped filling things in and looked broken at random.
  const [youtubeNotice, setYoutubeNotice] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [activeTab, setActiveTab] = useState('search');
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  // When set, the profile shows only videos this person and that one are both
  // on. Clicking a row in the collaborators table sets it.
  const [pairWith, setPairWith] = useState(null);
  // Public video search: creators to require, and free text to match on title.
  const [videoFilterCreators, setVideoFilterCreators] = useState([]);
  const [videoFilterText, setVideoFilterText] = useState('');
  const [creatorPickerText, setCreatorPickerText] = useState('');
  // What the last channel scan found, so the count is visible without counting.
  const [bulkScanSummary, setBulkScanSummary] = useState('');
  // Names a bulk import found but did not create, for review afterwards.
  const [proposedCreators, setProposedCreators] = useState([]);
  // Channels hidden in the scan preview, so one you have dealt with gets out
  // of the way without losing what you selected from it.
  const [collapsedChannels, setCollapsedChannels] = useState(new Set());
  // Hide scan results that name nobody already tracked, which is the closest
  // usable test for whether a video is a collaboration.
  const [onlyLikelyCollabs, setOnlyLikelyCollabs] = useState(false);
  // Single-creator videos being reviewed by hand, and which suggestions are
  // ticked on each.
  const [soloReview, setSoloReview] = useState([]);
  const [reviewPicks, setReviewPicks] = useState({});
  const [reviewLoading, setReviewLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newAuthor, setNewAuthor] = useState({ name: '', channel: '', photo: '', socialLinks: [{ platform: '', url: '' }] });
  const [newVideo, setNewVideo] = useState({ title: '', url: '', date: '', authorIds: [] });
  const [newCreatorName, setNewCreatorName] = useState('');
  const [showAddCreatorInline, setShowAddCreatorInline] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [suggestedVideos, setSuggestedVideos] = useState([]);
  const [newSuggestion, setNewSuggestion] = useState({ url: '' });
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [suggestionSubmitted, setSuggestionSubmitted] = useState(false);
  const [pendingCollaborators, setPendingCollaborators] = useState({ existing: [], toCreate: [] });

  // Bulk import state
  const [bulkImportUrl, setBulkImportUrl] = useState('');
  const [fetchedVideos, setFetchedVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [isLoadingBulk, setIsLoadingBulk] = useState(false);
  const [bulkImportProgress, setBulkImportProgress] = useState({ current: 0, total: 0 });

  // Bulk delete state
  const [selectedCreators, setSelectedCreators] = useState(new Set());
  const [selectedVideoIds, setSelectedVideoIds] = useState(new Set());
  const [showDuplicates, setShowDuplicates] = useState(false);

  // Rescan collaborators state
  const [rescanProgress, setRescanProgress] = useState({ current: 0, total: 0 });
  const [isRescanning, setIsRescanning] = useState(false);
  const [selectedRescanVideos, setSelectedRescanVideos] = useState(new Set());

  // Version state
  const [appVersion, setAppVersion] = useState('Loading...');

  // No ADMIN_PASSWORD here any more. Create React App inlines every
  // REACT_APP_* value into the JavaScript bundle at build time, so the admin
  // password was being shipped to every visitor in readable form. The password
  // now only exists as ADMIN_PASSWORD in the server environment, and the
  // browser never sees it: it posts an attempt to /api/auth and is told yes or
  // no.
  /*
   * The address bar and the browser's back button.
   *
   * The app had no URL state at all: every view lived in React state alone, so
   * nothing could be linked, bookmarked, or reached with Back. The first
   * attempt at fixing that used replaceState throughout, which put the state
   * in the URL but created no history entries, so Back still left the site
   * entirely. Worse than before, arguably, because the URL then looked like it
   * ought to work.
   *
   * So: state goes to the URL, and the URL is the source of truth when the
   * user navigates. pushState for anything that reads as going somewhere,
   * replaceState for refining what is already on screen.
   *
   * The guard matters. Writing state to the URL and reading the URL back into
   * state will loop forever unless the write is skipped when the change came
   * from the browser rather than from a click.
   */
  const restoringFromUrl = useRef(false);
  const readInitialUrl = useRef(false);
  const lastUrl = useRef(null);

  /*
   * Suggested fixes for creator records, from the same checks as
   * scripts/check-profiles.mjs. Recomputed when the creator or video data
   * changes, which is also what makes a fix disappear once it is applied.
   */
  const profileIssues = useMemo(() => {
    if (authors.length === 0) return [];
    const withCounts = authors.map((a) => ({
      ...a,
      videoCount: videos.filter((v) => v.authorIds.includes(a.id)).length,
    }));
    return findProfileIssues(withCounts);
  }, [authors, videos]);

  /** Merge one creator into another, optionally renaming the survivor. */
  const applyMergeSuggestion = async (suggestion) => {
    const { keep, drop, rename } = suggestion;
    const what =
      `Merge "${drop.name}" (${drop.videoCount} videos) into "${keep.name}" (${keep.videoCount} videos)?` +
      (rename ? `\n\nAnd rename the result to "${rename}".` : '') +
      '\n\nVideos and links move across, then the duplicate is deleted. This cannot be undone from here.';
    if (!window.confirm(what)) return;

    try {
      const res = await fetch('/api/creators', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ primaryId: keep.id, mergeIds: [drop.id] }),
      });
      if (res.status === 401) {
        alert('Your admin session expired. Log in again.');
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Merge failed');

      if (rename) {
        await fetch('/api/creators', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: keep.id,
            name: rename,
            channel: keep.channel,
            photo: keep.photo,
            socialLinks: keep.socialLinks || [],
          }),
        });
      }
      await refreshData();
    } catch (e) {
      alert(`Could not merge: ${e.message}`);
    }
  };

  const applyRenameSuggestion = async (creator) => {
    const next = window.prompt(`New name for "${creator.name}":`, creator.name);
    if (!next || next.trim() === creator.name) return;
    try {
      const res = await fetch('/api/creators', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: creator.id,
          name: next.trim(),
          channel: creator.channel,
          photo: creator.photo,
          socialLinks: creator.socialLinks || [],
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Rename failed');
      await refreshData();
    } catch (e) {
      alert(`Could not rename: ${e.message}`);
    }
  };

  const applyDeleteSuggestion = async (creator) => {
    if (
      !window.confirm(
        `Delete "${creator.name}"?\n\nIt will be removed from ${creator.videoCount} video(s). ` +
          'The videos themselves are kept. This cannot be undone from here.'
      )
    )
      return;
    try {
      const res = await fetch(`/api/creators?id=${creator.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Delete failed');
      await refreshData();
    } catch (e) {
      alert(`Could not delete: ${e.message}`);
    }
  };

  /** Create one of the collaborators a bulk import proposed but did not add. */
  const createProposedCreator = async (proposal) => {
    try {
      const fullUrl = handleToUrl(proposal.url);
      const res = await fetch('/api/creators', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: proposal.name,
          channel: fullUrl || '',
          // No photo. npm run avatars fills these in properly and hosts them
          // here; a generated one now would just have to be replaced.
          photo: '',
          socialLinks: fullUrl ? [{ platform: detectPlatform(fullUrl), url: fullUrl }] : [],
        }),
      });
      if (res.status === 401) {
        alert('Your admin session expired. Log in again.');
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      const created = await res.json();
      setAuthors((prev) => [...prev, created]);
      setProposedCreators((prev) => prev.filter((x) => x.name !== proposal.name));
    } catch (e) {
      alert(`Could not create "${proposal.name}": ${e.message}`);
    }
  };

  /*
   * Reviewing single-creator videos by hand.
   *
   * Automatic detection recovered three of a hundred, which is what a YouTube
   * description supports: it is a credits list mixed with advertising, and
   * telling "was in this video" from "is worth a follow" usually needs a
   * person. So this loads the descriptions, offers what it can see, and lets
   * you decide.
   */
  const loadSoloVideosForReview = async () => {
    setReviewLoading(true);
    try {
      const solo = videos.filter((v) => (v.authorIds || []).length <= 1 && youtubeIdOf(v.url));
      const ids = [...new Set(solo.map((v) => youtubeIdOf(v.url)))];
      const snippets = new Map();
      for (let i = 0; i < ids.length; i += 50) {
        const data = await youtubeApi('videos', { part: 'snippet', id: ids.slice(i, i + 50).join(',') });
        for (const item of data.items || []) {
          snippets.set(item.id, {
            title: item.snippet.title || '',
            description: item.snippet.description || '',
            channelTitle: item.snippet.channelTitle || '',
          });
        }
      }

      const profiles = buildProfiles(authors);
      const rows = [];
      for (const v of solo) {
        const snip = snippets.get(youtubeIdOf(v.url));
        if (!snip) continue;
        const suggestions = suggestCollaborators(snip, profiles, v.authorIds || []);
        if (suggestions.length) rows.push({ video: v, suggestions });
      }
      // Strongest evidence first, then most suggestions, so the clearest
      // decisions come before the ones needing thought.
      rows.sort((a, b) => {
        const s = (r) => r.suggestions.filter((x) => x.strength === 'strong').length;
        return s(b) - s(a) || b.suggestions.length - a.suggestions.length;
      });
      setSoloReview(rows);
      if (rows.length === 0) alert('No single-creator video names anybody else.');
    } catch (e) {
      alert(`Could not load: ${e.message}`);
    } finally {
      setReviewLoading(false);
    }
  };

  /** Attach chosen creators to a video and drop it from the review list. */
  const attachCollaborators = async (video, creatorIds) => {
    try {
      const res = await fetch('/api/videos', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: video.id,
          title: video.title,
          url: video.url,
          date: video.date,
          authorIds: [...new Set([...(video.authorIds || []), ...creatorIds])],
        }),
      });
      if (res.status === 401) {
        alert('Your admin session expired. Log in again.');
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      const updated = await res.json();
      setVideos((prev) => prev.map((v) => (v.id === updated.id ? { ...v, ...updated } : v)));
      setSoloReview((prev) => prev.filter((r) => r.video.id !== video.id));
    } catch (e) {
      alert(`Could not update: ${e.message}`);
    }
  };

  const refreshData = async () => {
    const [c, v] = await Promise.all([
      fetch('/api/creators').then((r) => r.json()),
      fetch('/api/videos').then((r) => r.json()),
    ]);
    setAuthors(c);
    setVideos(v);
  };

  const currentView = () => ({
    tab: activeTab,
    creatorId: selectedAuthor ? selectedAuthor.id : null,
    pairWith,
    videoCreators: videoFilterCreators,
  });

  const buildUrl = () => {
    const qs = searchForView(currentView());
    return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  };

  /** Put the app into whatever state a URL describes. */
  const applyUrl = (search) => {
    const view = viewFromSearch(search, (id) => authors.some((a) => a.id === id));
    setSelectedAuthor(view.creatorId ? authors.find((a) => a.id === view.creatorId) : null);
    setPairWith(view.pairWith);
    if (view.tab === 'find-videos') setVideoFilterCreators(view.videoCreators);
    setActiveTab(view.tab);
  };

  // Read the URL once, after the data it refers to has arrived.
  useEffect(() => {
    if (readInitialUrl.current || authors.length === 0) return;
    readInitialUrl.current = true;
    restoringFromUrl.current = true;
    lastUrl.current = window.location.pathname + window.location.search;
    applyUrl(window.location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authors]);

  // Back and Forward.
  useEffect(() => {
    const onPop = () => {
      restoringFromUrl.current = true;
      lastUrl.current = window.location.pathname + window.location.search;
      applyUrl(window.location.search);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authors]);

  // State to URL.
  useEffect(() => {
    if (!readInitialUrl.current) return;
    if (restoringFromUrl.current) {
      // This change came FROM the browser, so writing it back would either
      // loop or push a duplicate entry over the one just navigated to.
      restoringFromUrl.current = false;
      return;
    }
    const url = buildUrl();
    if (url === lastUrl.current) return;

    // isNavigation decides whether Back should undo this. See src/lib/viewUrl.js.
    const previous = lastUrl.current
      ? viewFromSearch(lastUrl.current.split('?')[1] || '', () => true)
      : null;
    if (isNavigation(previous, currentView())) window.history.pushState(null, '', url);
    else window.history.replaceState(null, '', url);
    lastUrl.current = url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedAuthor, pairWith, videoFilterCreators]);

  // Ask the server whether this browser already holds a valid session.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth')
      .then((r) => (r.ok ? r.json() : { authenticated: false }))
      .then((d) => {
        if (!cancelled) setIsAdmin(Boolean(d.authenticated));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setAuthChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Fetch data from API on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [creatorsRes, videosRes] = await Promise.all([
          fetch('/api/creators'),
          fetch('/api/videos')
        ]);
        
        const creatorsData = await creatorsRes.json();
        const videosData = await videosRes.json();
        
        setAuthors(creatorsData);
        setVideos(videosData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fetch version on mount
  useEffect(() => {
    fetch('/version.json')
      .then(res => res.json())
      .then(data => setAppVersion(data.version))
      .catch(() => setAppVersion('1.0.0'));
  }, []);

  const ADMIN_TABS = ['videos', 'suggestions', 'authors', 'stats', 'admin-login'];

  // Whether the session check is still in flight on a tab that needs one.
  // Without this the admin sees the login screen for a moment on every reload,
  // because isAdmin now starts false and is confirmed by the server rather
  // than being read synchronously from localStorage.
  const authPending = () => !authChecked && ADMIN_TABS.includes(activeTab);

  const needsAdminAccess = () => authChecked && !isAdmin && ADMIN_TABS.includes(activeTab);

  // The password is checked by the server, which replies with an HttpOnly
  // cookie. Nothing about the outcome is stored where the page can edit it.
  const handleAdminLogin = async () => {
    setLoginError('');
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.authenticated) {
        setIsAdmin(true);
        setAdminPassword('');
        setActiveTab('videos');
        return;
      }
      // Say what actually went wrong. A misconfigured server and a wrong
      // password are different problems, and only one of them is the user's.
      setLoginError(data.error || 'Incorrect password');
      setAdminPassword('');
    } catch (e) {
      setLoginError('Could not reach the server. Check your connection.');
    }
  };

  const handleAdminLogout = async () => {
    try {
      await fetch('/api/auth', { method: 'DELETE' });
    } catch (e) {
      // Clearing local state still matters even if the request failed.
    }
    setIsAdmin(false);
    setActiveTab('search');
  };

  // Helper function to detect platform from URL
  const detectPlatform = (url) => {
    if (!url) return '';
    const urlLower = url.toLowerCase();
    if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) return 'YouTube';
    if (urlLower.includes('instagram.com')) return 'Instagram';
    if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) return 'Twitter';
    if (urlLower.includes('tiktok.com')) return 'TikTok';
    if (urlLower.includes('twitch.tv')) return 'Twitch';
    if (urlLower.includes('facebook.com')) return 'Facebook';
    return 'Other';
  };

  // Helper function to get platform icon
  const getPlatformIcon = (url) => {
    const platform = detectPlatform(url);
    const icons = {
      'YouTube': '📺',
      'Instagram': '📷',
      'Twitter': '🐦',
      'TikTok': '🎵',
      'Twitch': '🎮',
      'Facebook': '👥',
      'Other': '🔗'
    };
    return icons[platform] || '🔗';
  };

  // Helper function to get social links for a creator
  const getSocialLinks = (author) => {
    if (author.socialLinks && Array.isArray(author.socialLinks)) {
      return author.socialLinks;
    }
    // Fallback to old channel field
    if (author.channel) {
      return [{ platform: detectPlatform(author.channel), url: author.channel }];
    }
    return [];
  };

  // Social link management for new author
  const addSocialLinkToAuthor = () => {
    setNewAuthor({
      ...newAuthor,
      socialLinks: [...newAuthor.socialLinks, { platform: '', url: '' }]
    });
  };

  const removeSocialLinkFromAuthor = (index) => {
    setNewAuthor({
      ...newAuthor,
      socialLinks: newAuthor.socialLinks.filter((_, i) => i !== index)
    });
  };

  const updateSocialLink = (index, field, value) => {
    const updatedLinks = [...newAuthor.socialLinks];
    updatedLinks[index][field] = value;

    // Auto-detect platform when URL changes
    if (field === 'url') {
      updatedLinks[index].platform = detectPlatform(value);
    }

    setNewAuthor({
      ...newAuthor,
      socialLinks: updatedLinks
    });
  };

  // Social link management for editing author
  const addSocialLinkToEditingAuthor = () => {
    setEditingAuthor({
      ...editingAuthor,
      socialLinks: [...(editingAuthor.socialLinks || []), { platform: '', url: '' }]
    });
  };

  const removeSocialLinkFromEditingAuthor = (index) => {
    setEditingAuthor({
      ...editingAuthor,
      socialLinks: editingAuthor.socialLinks.filter((_, i) => i !== index)
    });
  };

  const updateEditingSocialLink = (index, field, value) => {
    const updatedLinks = [...editingAuthor.socialLinks];
    updatedLinks[index][field] = value;

    // Auto-detect platform when URL changes
    if (field === 'url') {
      updatedLinks[index].platform = detectPlatform(value);
    }

    setEditingAuthor({
      ...editingAuthor,
      socialLinks: updatedLinks
    });
  };
// NEW: Fetch YouTube channel info by channel name or URL
const fetchYouTubeChannelInfo = async (channelInput) => {
  // The key is no longer visible from here, so the client cannot pre-check it.
  // /api/youtube reports a missing key as a 503 with reason "notConfigured",
  // which reaches the notice banner rather than being logged and forgotten.

  try {
    let channelId = null;
    
    // Check if input is a YouTube channel URL
    if (channelInput.includes('youtube.com/')) {
      if (channelInput.includes('/channel/')) {
        channelId = channelInput.split('/channel/')[1]?.split('/')[0]?.split('?')[0];
      } else if (channelInput.includes('/@')) {
        // Handle @username format
        const username = channelInput.split('/@')[1]?.split('/')[0]?.split('?')[0];
        const searchData = await youtubeApi('channels', { part: 'snippet', forHandle: username });
        if (searchData.items && searchData.items.length > 0) {
          channelId = searchData.items[0].id;
        }
      }
    } else {
      // Search by channel name. Note this is the expensive one: a search costs
      // 100 quota units against a 10,000 daily allowance, where a direct
      // lookup costs 1. Prefer pasting a channel URL over typing a name.
      const searchData = await youtubeApi('search', {
        part: 'snippet',
        type: 'channel',
        q: channelInput,
        maxResults: 1,
      });
      if (searchData.items && searchData.items.length > 0) {
        channelId = searchData.items[0].snippet.channelId;
      }
    }

    if (!channelId) {
      return null;
    }

    // Fetch detailed channel info
    const data = await youtubeApi('channels', { part: 'snippet', id: channelId });

   if (data.items && data.items.length > 0) {
  const channel = data.items[0];
  // Use custom URL if available, otherwise fall back to channel ID
  const customUrl = channel.snippet.customUrl;
  const channelUrl = customUrl 
    ? `https://www.youtube.com/@${customUrl.replace('@', '')}` 
    : `https://www.youtube.com/channel/${channel.id}`;
  
  return {
    name: channel.snippet.title,
    photo: channel.snippet.thumbnails.high?.url || channel.snippet.thumbnails.default?.url,
    channelUrl: channelUrl
  };
}

    return null;
  } catch (error) {
    console.error('Error fetching YouTube channel info:', error);
    return null;
  }
};

// Fetch Instagram profile picture from username (via backend to avoid CORS)
const fetchInstagramProfilePic = async (instagramUrl) => {
  try {
    // Extract username from Instagram URL
    let username = null;

    if (instagramUrl.includes('instagram.com/')) {
      const urlParts = instagramUrl.split('instagram.com/')[1]?.split('/')[0]?.split('?')[0];
      username = urlParts;
    } else {
      // If it's just a username without URL
      username = instagramUrl;
    }

    if (!username) {
      return null;
    }

    console.log('Fetching Instagram profile via backend API for:', username);

    // Call our backend API endpoint (server-side fetch to avoid CORS)
    const response = await fetch(`/api/instagram?username=${encodeURIComponent(username)}`);

    if (!response.ok) {
      console.warn('Instagram API returned non-OK status:', response.status);
      return {
        photo: null,
        name: username
      };
    }

    const data = await response.json();

    if (data && data.photo) {
      console.log('Successfully fetched Instagram profile:', data);
      return {
        name: data.name || username,
        photo: data.photo,
        channelUrl: `https://www.instagram.com/${username}/`
      };
    }

    // If no photo was found, return username only
    return {
      photo: null,
      name: data.name || username,
      channelUrl: `https://www.instagram.com/${username}/`
    };
  } catch (error) {
    console.error('Error fetching Instagram profile info:', error);
    return null;
  }
};
// NEW: Detect collaborators from video title
// NEW: Detect collaborators from video title and create if needed
// NEW: Detect collaborators from video title and create if needed
// ENHANCED: Detect collaborators from video title with multiple patterns
// ENHANCED: Detect collaborators from video title and description with multiple patterns
const detectCollaboratorsFromTitle = async (title, description = '', mainCreatorName = '') => {
  console.log('🔍 Detecting collaborators from title:', title);
  if (description) {
    console.log('🔍 Also checking description:', description.substring(0, 200) + '...');
  }
  if (mainCreatorName) {
    console.log('🔍 Excluding main creator:', mainCreatorName);
  }

  const detectedCreators = [];
  const creatorsToCreate = [];
  const potentialNames = new Set();
  const socialHandles = new Map(); // Map name to social handle

  // For description: extract targeted sections only (cast, featuring, etc.)
  let descriptionCollabSection = '';
  if (description) {
    // Debug: Show the raw description around "Starring"
    const starringIndex = description.indexOf('Starring');
    if (starringIndex !== -1) {
      const snippet = description.substring(starringIndex, starringIndex + 300);
      console.log('🔍 Raw description around "Starring:"');
      console.log(JSON.stringify(snippet)); // Use JSON.stringify to see newlines as \n
    }

    // Look for sections that indicate collaborators
    // More flexible patterns that allow extra words after the section name
    // Handle both "STARRING:" and "STARRING" (with or without colon)
    // Stop at new section headers that have lowercase letters (e.g., "Executive Producers:")
    // This ensures we don't stop at blank lines or all-caps labels like "ALEX:"
    // Extract collaboration sections - capture everything after the section header
    // Allow content on same line OR next line after the section header
    // Stop at: double newline, end of string, or next section header (all caps word followed by colon), or hashtags
    // Use non-greedy matching with specific stop conditions
    // Stop at: blank line, end of string, next section header (with "By:" or all caps), or hashtags
    const sectionPatterns = [
      /cast[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /featuring[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /credits[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /collaborators?[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /appearances?[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /guests?[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /performed by[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /starring[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /talent[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /performers?[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
      /actors?[^:\n]*:?\s*\n?([^]+?)(?=\n\s*\n|\n[A-Z][a-z]+\s+By:|$|\n#)/i,
    ];

    for (const pattern of sectionPatterns) {
      const match = description.match(pattern);
      if (match) {
        descriptionCollabSection += match[1] + ' ';
        console.log('✅ Found collab section:', match[0].substring(0, 300));
        console.log('   Extracted text (first 300 chars):', match[1].substring(0, 300));
        console.log('   Extracted text (full length):', match[1].length);
      } else {
        // Test if the pattern name exists but doesn't match
        const patternSource = pattern.source;
        const sectionName = patternSource.match(/^\/?([\w\s]+)\[/)?.[1];
        if (sectionName && description.toLowerCase().includes(sectionName)) {
          console.log(`⚠️ Found "${sectionName}" in description but pattern didn't match`);
        }
      }
    }

    if (!descriptionCollabSection) {
      console.log('⚠️ No collaboration section found in description');
    }

    if (descriptionCollabSection) {
      console.log('Full collab section text:', descriptionCollabSection);
    }
  }

  // For name|handle extraction, use ONLY description (title causes false matches)
  // Clean Unicode control characters that can interfere with regex matching
  const descriptionOnlyText = descriptionCollabSection
    .replace(/[\u200B-\u200D\u202A-\u202E\uFEFF\u2068\u2069]/g, '') // Remove zero-width, directional, and isolate characters
    .replace(/\u00A0/g, ' ') // Replace non-breaking space with regular space
    .replace(/[ \t]+/g, ' '); // Normalize multiple spaces/tabs to single space (but preserve newlines!)

  // DISABLED: @mentions pattern was too broad and caught handles as names
  // Pattern 1: @mentions (but not YouTube channel IDs which start with UC)
  // const mentionRegex = /@(\w+)/g;
  // const mentions = [...combinedText.matchAll(mentionRegex)];
  // mentions.forEach(match => {
  //   const handle = match[1];
  //   // Skip YouTube channel IDs (start with UC and are long alphanumeric strings)
  //   if (!handle.startsWith('UC') && !handle.match(/^[A-Z0-9_-]{20,}$/)) {
  //     potentialNames.add(handle);
  //   }
  // });
  // console.log('Found @mentions:', [...mentions.map(m => m[1])]);

  // Pattern 1b: Names followed by social links (e.g., "Name | /username" or "Name | instagram.com/username")
  // Common in cast/credits sections - extract both name AND the social handle
  // Multiple approaches to catch different formats

  // Approach 1: Instagram URLs (may appear anywhere on the line after the name)
  // Accepts pipe |, lowercase l, or colon : as separator
  const nameSocialRegex1 = /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:].*?https?:\/\/(?:www\.)?instagram\.com\/([a-zA-Z0-9._-]+)/gm;

  // Approach 2: Twitter/X URLs (may appear anywhere on the line after the name)
  const nameSocialRegex2 = /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:].*?https?:\/\/(?:www\.)?(?:twitter\.com|x\.com)\/([a-zA-Z0-9._-]+)/gm;

  // Approach 3: TikTok URLs (may appear anywhere on the line after the name)
  const nameSocialRegex3 = /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:].*?https?:\/\/(?:www\.)?tiktok\.com\/@?([a-zA-Z0-9._-]+)/gm;

  // Approach 4: Simple /handle or / handle format (with optional space after /)
  const nameSocialRegex4 = /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:]\s*\/\s*([a-zA-Z0-9._-]+)/gm;

  // Approach 5: @handle or @ handle format (with optional space after @)
  const nameSocialRegex5 = /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:]\s*@\s*([a-zA-Z0-9._-]+)/gm;

  // Approach 6: For YouTube URLs or any URL we can't extract handle from - capture name AND full URL
  const nameOnlyRegex = /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:]\s*(https?:\/\/[^\s]+)/gm;

  // Approach 7: Handle "CHARACTER: Name | /handle" or "CHARACTER: Name | / handle" format (with optional space after /)
  const characterNameSocialRegex = /^[A-Z\s]+:\s*([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:]\s*\/\s*([a-zA-Z0-9._-]+)/gm;

  // Approach 8: Handle "CHARACTER: Name | @handle" or "CHARACTER: Name | @ handle" format (with optional space after @)
  const characterNameAtHandleRegex = /^[A-Z\s]+:\s*([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:]\s*@\s*([a-zA-Z0-9._-]+)/gm;

  // Approach 9: Handle "CHARACTER: Name | instagram.com/handle" format
  const characterNameInstagramRegex = /^[A-Z\s]+:\s*([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,2})\s*[|l:].*?https?:\/\/(?:www\.)?instagram\.com\/([a-zA-Z0-9._-]+)/gm;

  console.log('Testing regex on text:', descriptionOnlyText.substring(0, 200));

  const nameSocialMatches1 = [...descriptionOnlyText.matchAll(nameSocialRegex1)];
  const nameSocialMatches2 = [...descriptionOnlyText.matchAll(nameSocialRegex2)];
  const nameSocialMatches3 = [...descriptionOnlyText.matchAll(nameSocialRegex3)];
  const nameSocialMatches4 = [...descriptionOnlyText.matchAll(nameSocialRegex4)];
  const nameSocialMatches5 = [...descriptionOnlyText.matchAll(nameSocialRegex5)];
  const nameOnlyMatches = [...descriptionOnlyText.matchAll(nameOnlyRegex)];
  const characterNameMatches = [...descriptionOnlyText.matchAll(characterNameSocialRegex)];
  const characterAtHandleMatches = [...descriptionOnlyText.matchAll(characterNameAtHandleRegex)];
  const characterInstagramMatches = [...descriptionOnlyText.matchAll(characterNameInstagramRegex)];

  // Approach 10: Standalone @mentions (common in video descriptions)
  // Matches patterns like "thanks to @Danny-Gonzalez" or "@username for helping"
  const standaloneMentionRegex = /@([a-zA-Z0-9._-]+)/g;
  const standaloneMentionMatches = [...descriptionOnlyText.matchAll(standaloneMentionRegex)];

  console.log('Regex matches found:', {
    instagram: nameSocialMatches1.length,
    twitter: nameSocialMatches2.length,
    tiktok: nameSocialMatches3.length,
    slash: nameSocialMatches4.length,
    atHandle: nameSocialMatches5.length,
    nameOnly: nameOnlyMatches.length,
    characterSlash: characterNameMatches.length,
    characterAt: characterAtHandleMatches.length,
    characterInstagram: characterInstagramMatches.length,
    standaloneMentions: standaloneMentionMatches.length
  });

  // Combine all approaches - handle+name and name-only
  const allMatches = [
    ...nameSocialMatches1,
    ...nameSocialMatches2,
    ...nameSocialMatches3,
    ...nameSocialMatches4,
    ...nameSocialMatches5,
    ...characterNameMatches,
    ...characterAtHandleMatches,
    ...characterInstagramMatches
  ];

  // Process name+handle matches
  allMatches.forEach(match => {
    const name = match[1].trim();
    const handle = match[2].trim();

    // Filter out invalid handles (domain names, not actual handles)
    const invalidHandles = ['www', 'com', 'instagram', 'twitter', 'tiktok', 'youtube', 'http', 'https'];
    // Note: Periods are allowed in handles (e.g., hi.poppylaur), so we only reject if it's a domain keyword
    const isInvalidHandle = invalidHandles.includes(handle.toLowerCase());

    if (name && name.length > 2 && handle && !isInvalidHandle) {
      potentialNames.add(name);
      // Store the social handle for this name
      socialHandles.set(name, handle);
      console.log(`Mapped "${name}" to handle "@${handle}"`);
    } else if (isInvalidHandle) {
      console.log(`Skipped invalid handle: "${handle}" for name "${name}"`);
    }
  });

  // Process name-only matches (for YouTube URLs where we can't extract handle)
  nameOnlyMatches.forEach(match => {
    const name = match[1].trim();
    const url = match[2].trim();
    if (name && name.length > 2 && !potentialNames.has(name)) {
      potentialNames.add(name);
      // Store the full URL for this name
      socialHandles.set(name, url);
      console.log(`Added name with URL: "${name}" → ${url}`);
    }
  });

  // Process standalone @mentions - look them up in the database by handle
  standaloneMentionMatches.forEach(match => {
    const handle = match[1].trim();

    // Skip if it's a common word or too short
    const invalidHandles = ['www', 'com', 'instagram', 'twitter', 'tiktok', 'youtube', 'http', 'https'];
    if (invalidHandles.includes(handle.toLowerCase()) || handle.length < 3) {
      return;
    }

    console.log(`Found standalone @mention: @${handle}`);

    // Look up in database by matching handle against social links
    const creator = authors.find(author => {
      if (!author.socialLinks || author.socialLinks.length === 0) return false;

      return author.socialLinks.some(link => {
        if (!link.url) return false;
        const urlLower = link.url.toLowerCase();
        const handleLower = handle.toLowerCase();

        // Check if the URL contains this handle
        return urlLower.includes(`/@${handleLower}`) ||
               urlLower.includes(`/${handleLower}`) ||
               urlLower.endsWith(handleLower);
      });
    });

    if (creator) {
      console.log(`Matched @${handle} to existing creator: ${creator.name}`);
      potentialNames.add(creator.name);
      socialHandles.set(creator.name, handle);
    } else {
      console.log(`No creator found for @${handle}, will suggest creating`);
      // Store just the handle for potential creation
      potentialNames.add(handle);
      socialHandles.set(handle, handle);
    }
  });

  // Approach 11: @mentions in titles
  // Extract @handles from titles and look them up in the database
  const titleMentionRegex = /@([a-zA-Z0-9._-]+)/g;
  const titleMentionMatches = [...title.matchAll(titleMentionRegex)];

  titleMentionMatches.forEach(match => {
    const handle = match[1].trim();

    // Skip if it's a common word or too short
    const invalidHandles = ['www', 'com', 'instagram', 'twitter', 'tiktok', 'youtube', 'http', 'https'];
    if (invalidHandles.includes(handle.toLowerCase()) || handle.length < 3) {
      return;
    }

    console.log(`Found @mention in title: @${handle}`);

    // Look up in database by matching handle against social links
    const creator = authors.find(author => {
      if (!author.socialLinks || author.socialLinks.length === 0) return false;

      return author.socialLinks.some(link => {
        if (!link.url) return false;
        const urlLower = link.url.toLowerCase();
        const handleLower = handle.toLowerCase();

        // Check if the URL contains this handle
        return urlLower.includes(`/@${handleLower}`) ||
               urlLower.includes(`/${handleLower}`) ||
               urlLower.endsWith(handleLower);
      });
    });

    if (creator) {
      console.log(`Matched @${handle} in title to existing creator: ${creator.name}`);
      potentialNames.add(creator.name);
      socialHandles.set(creator.name, handle);
    } else {
      console.log(`No creator found for @${handle} in title, will suggest creating`);
      // Store just the handle for potential creation
      potentialNames.add(handle);
      socialHandles.set(handle, handle);
    }
  });

  // Approach 12: "w/", "with", "feat.", "ft." patterns in titles
  // Matches patterns like "(w/ Danny Gonzalez)", "(with Danny Gonzalez)", "(feat. NAME)", "ft. NAME", or "w/ @handle"
  // Use greedy matching to capture multiple names separated by &, and, etc.
  const withSlashRegex = /\bw\/\s*@?([a-zA-Z0-9._\s&-]+)(?:[)\]|,]|$)/gi;
  const withParenRegex = /\(with\s+@?([a-zA-Z0-9._\s&-]+)\)/gi;
  const withSpaceRegex = /\bwith\s+@?([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)\b/g;
  const featRegex = /\((?:feat\.|ft\.|featuring)\s+@?([a-zA-Z0-9._\s&-]+)\)/gi;
  const featNoParen = /\b(?:feat\.|ft\.)\s+@?([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)\b/g;

  const withSlashMatches = [...title.matchAll(withSlashRegex)];
  const withParenMatches = [...title.matchAll(withParenRegex)];
  const withSpaceMatches = [...title.matchAll(withSpaceRegex)];
  const featMatches = [...title.matchAll(featRegex)];
  const featNoParenMatches = [...title.matchAll(featNoParen)];

  [...withSlashMatches, ...withParenMatches, ...withSpaceMatches, ...featMatches, ...featNoParenMatches].forEach(match => {
    let nameOrHandle = match[1].trim();

    // Check if it starts with @ (it's a handle)
    const isHandle = match[0].includes('@');

    if (isHandle) {
      // It's a handle - look it up in the database
      const handle = nameOrHandle.replace(/@/g, '');

      const creator = authors.find(author => {
        if (!author.socialLinks || author.socialLinks.length === 0) return false;

        return author.socialLinks.some(link => {
          if (!link.url) return false;
          const urlLower = link.url.toLowerCase();
          const handleLower = handle.toLowerCase();

          return urlLower.includes(`/@${handleLower}`) ||
                 urlLower.includes(`/${handleLower}`) ||
                 urlLower.endsWith(handleLower);
        });
      });

      if (creator) {
        console.log(`Found "w/with/feat @handle" pattern: @${handle} → ${creator.name}`);
        potentialNames.add(creator.name);
        socialHandles.set(creator.name, handle);
      } else {
        console.log(`Found "w/with/feat @handle" pattern: @${handle} (new creator)`);
        potentialNames.add(handle);
        socialHandles.set(handle, handle);
      }
    } else {
      // It's a name - split on &, and, x, or comma separators
      const names = nameOrHandle.split(/\s+(?:&|and|x)\s+|,\s*/);

      names.forEach(rawName => {
        let name = rawName.replace(/[)\],.!?]+$/, '').trim();

        if (name && name.length > 2) {
          console.log(`Found "w/with/feat" pattern: ${name}`);
          potentialNames.add(name);
        }
      });
    }
  });

  // Approach 12: Guest/Featuring labels in description
  // Matches patterns like "Guest: @handle", "Featuring: / handle", or "Guest: http://instagram.com/handle"
  // Search the full description, not just collab sections, since these labels often appear at the top
  const guestLabelRegex = /(?:guest|featuring|feat\.|ft\.):\s*(?:(?:https?:\/\/)?(?:www\.)?(?:instagram\.com|twitter\.com|x\.com|tiktok\.com|youtube\.com)\/(@?[a-zA-Z0-9._-]+)|@([a-zA-Z0-9._-]+)|\/\s*([a-zA-Z0-9._-]+))/gi;

  console.log('🔍 Searching for guest labels in description...');
  console.log('Description preview:', description.substring(0, 300));

  const guestLabelMatches = [...description.matchAll(guestLabelRegex)];
  console.log('Raw guest label matches:', guestLabelMatches);

  guestLabelMatches.forEach(match => {
    // The regex has 3 capture groups, find which one matched
    const handle = (match[1] || match[2] || match[3]).trim().replace(/^@/, '');

    // Skip if it's a common word or too short
    const invalidHandles = ['www', 'com', 'instagram', 'twitter', 'tiktok', 'youtube', 'http', 'https', 'discord', 'prettymuchit'];
    if (invalidHandles.includes(handle.toLowerCase()) || handle.length < 3) {
      return;
    }

    console.log(`Found guest/featuring label with handle: ${handle}`);

    // Look up in database by matching handle against social links
    const creator = authors.find(author => {
      if (!author.socialLinks || author.socialLinks.length === 0) return false;

      return author.socialLinks.some(link => {
        if (!link.url) return false;
        const urlLower = link.url.toLowerCase();
        const handleLower = handle.toLowerCase();

        // Check if the URL contains this handle
        return urlLower.includes(`/@${handleLower}`) ||
               urlLower.includes(`/${handleLower}`) ||
               urlLower.endsWith(handleLower);
      });
    });

    if (creator) {
      console.log(`Matched guest label handle to existing creator: ${creator.name}`);
      potentialNames.add(creator.name);
      socialHandles.set(creator.name, handle);
    } else {
      console.log(`No creator found for handle ${handle}, will suggest creating`);
      potentialNames.add(handle);
      socialHandles.set(handle, handle);
    }
  });

  console.log('Found name|social format (approach 1 - Instagram):', [...nameSocialMatches1.map(m => `${m[1]} → ${m[2]}`)]);
  console.log('Found name|social format (approach 2 - Twitter/X):', [...nameSocialMatches2.map(m => `${m[1]} → ${m[2]}`)]);
  console.log('Found name|social format (approach 3 - TikTok):', [...nameSocialMatches3.map(m => `${m[1]} → ${m[2]}`)]);
  console.log('Found name|social format (approach 4 - /handle):', [...nameSocialMatches4.map(m => `${m[1]} → ${m[2]}`)]);
  console.log('Found name-only (approach 5 - any URL):', [...nameOnlyMatches.map(m => m[1])]);
  console.log('Found standalone @mentions in description (approach 10):', [...standaloneMentionMatches.map(m => `@${m[1]}`)]);
  console.log('Found @mentions in title (approach 11):', [...titleMentionMatches.map(m => `@${m[1]}`)]);
  console.log('Found "w/with/feat" pattern in title (approach 12):', [...[...withSlashMatches, ...withParenMatches, ...withSpaceMatches, ...featMatches, ...featNoParenMatches].map(m => m[1])]);
  console.log('Found guest/featuring labels (approach 13):', [...guestLabelMatches.map(m => m[1])]);

  // DISABLED: These patterns were too broad and caused false positives
  // They would detect random text from titles as collaborator names

  // // Pattern 2: ft. / feat. / featuring
  // const ftRegex = /(?:ft\.|feat\.|featuring)\s+([^(\-[\]|]+?)(?:\s*[(\-[\]|]|$)/gi;
  // const ftMatches = [...combinedText.matchAll(ftRegex)];
  // ftMatches.forEach(match => {
  //   const names = match[1].trim().split(/\s+(?:and|&|x)\s+|,\s*/);
  //   names.forEach(name => {
  //     const cleaned = name.trim().replace(/@/g, '');
  //     if (cleaned) potentialNames.add(cleaned);
  //   });
  // });
  // console.log('Found ft./feat.:', [...ftMatches.map(m => m[1])]);

  // // Pattern 3: with [names]
  // const withRegex = /\bwith\s+([^(\-[\]|!?.]+?)(?:\s*[(\-[\]|!?.]|$)/gi;
  // const withMatches = [...combinedText.matchAll(withRegex)];
  // withMatches.forEach(match => {
  //   const names = match[1].trim().split(/\s+(?:and|&|x)\s+|,\s*/);
  //   names.forEach(name => {
  //     const cleaned = name.trim().replace(/@/g, '');
  //     if (cleaned && cleaned.length > 2) potentialNames.add(cleaned);
  //   });
  // });
  // console.log('Found "with":', [...withMatches.map(m => m[1])]);

  // // Pattern 4: x / & / and between names
  // const separatorRegex = /\b([A-Z][\w\s]{2,20})\s+(?:x|&|and)\s+([A-Z][\w\s]{2,20})\b/g;
  // const separatorMatches = [...combinedText.matchAll(separatorRegex)];
  // separatorMatches.forEach(match => {
  //   [match[1], match[2]].forEach(name => {
  //     const cleaned = name.trim().replace(/@/g, '');
  //     if (cleaned && cleaned.length > 2) potentialNames.add(cleaned);
  //   });
  // });
  // console.log('Found separator names:', [...separatorMatches.flatMap(m => [m[1], m[2]])]);

  // // Pattern 5: Names in parentheses
  // const parenRegex = /\(([^)]+)\)/g;
  // const parenMatches = [...combinedText.matchAll(parenRegex)];
  // parenMatches.forEach(match => {
  //   const content = match[1].trim();
  //   // Only if it looks like a name (not too long, starts with capital or @)
  //   if (content.length < 30 && (content.match(/^[@A-Z]/) || content.includes('@'))) {
  //     const names = content.split(/\s+(?:and|&|x)\s+|,\s*/);
  //     names.forEach(name => {
  //       const cleaned = name.trim().replace(/@/g, '');
  //       if (cleaned && cleaned.length > 2) potentialNames.add(cleaned);
  //     });
  //   }
  // });
  // console.log('Found parentheses names:', [...parenMatches.map(m => m[1])]);

  // // Pattern 6: vs / versus (also disabled - too broad)
  // const vsRegex = /\b([A-Z][\w\s]{2,20})\s+(?:vs\.?|versus)\s+([A-Z][\w\s]{2,20})\b/gi;
  // const vsMatches = [...combinedText.matchAll(vsRegex)];
  // vsMatches.forEach(match => {
  //   [match[1], match[2]].forEach(name => {
  //     const cleaned = name.trim().replace(/@/g, '');
  //     if (cleaned && cleaned.length > 2) potentialNames.add(cleaned);
  //   });
  // });
  // console.log('Found vs/versus names:', [...vsMatches.flatMap(m => [m[1], m[2]])]);

  // DISABLED Pattern 7: Database creator matching
  // This pattern was adding people not even on the page by scanning the entire description
  // console.log('Checking all database creators against title and description...');
  // authors.forEach(author => {
  //   const authorName = author.name;
  //   const authorNameLower = authorName.toLowerCase();
  //   const textLower = combinedText.toLowerCase();

  //   // Check if the creator's full name appears in the text
  //   if (textLower.includes(authorNameLower)) {
  //     console.log(`Found database creator "${authorName}" in text`);
  //     potentialNames.add(authorName);
  //   }

  //   // Also check channel handle if it exists
  //   if (author.channel) {
  //     const channelHandle = author.channel.split('@').pop().split('/')[0];
  //     if (channelHandle && textLower.includes(channelHandle.toLowerCase())) {
  //       console.log(`Found database creator "${authorName}" by channel handle in text`);
  //       potentialNames.add(authorName);
  //     }
  //   }
  // });

  console.log('All potential names found:', [...potentialNames]);

  // Filter out invalid names (URLs, domains, channel IDs, too long, etc.)
  const invalidNames = ['www.instagram.com', 'instagram.com', 'twitter.com', 'tiktok.com', 'youtube.com'];
  for (const name of potentialNames) {
    const nameLower = name.toLowerCase();
    // Remove if it's:
    // - A URL/domain
    // - Contains newlines
    // - Too long (likely title text)
    // - YouTube channel ID (UC followed by alphanumeric)
    // - All uppercase and numbers (likely an ID)
    // - Contains only numbers
    const isChannelId = name.startsWith('UC') || name.match(/^[A-Z0-9_-]{20,}$/);
    const isNumericOnly = /^\d+$/.test(name);
    const isMostlyUppercaseAndNumbers = name.length > 10 && /^[A-Z0-9_-]+$/.test(name);

    if (invalidNames.includes(nameLower) ||
        name.includes('\n') ||
        name.includes('www.') ||
        name.length > 50 ||
        name.length < 2 ||
        isChannelId ||
        isNumericOnly ||
        isMostlyUppercaseAndNumbers) {
      console.log(`Filtering out invalid name: "${name.substring(0, 50)}"`);
      potentialNames.delete(name);
    }
  }

  // Filter out the main creator from potential names
  if (mainCreatorName) {
    const mainNameLower = mainCreatorName.toLowerCase();
    for (const name of potentialNames) {
      if (name.toLowerCase() === mainNameLower ||
          mainNameLower.includes(name.toLowerCase()) ||
          name.toLowerCase().includes(mainNameLower)) {
        console.log(`Excluding main creator: "${name}"`);
        potentialNames.delete(name);
      }
    }
  }

  // If no potential names found, return early
  if (potentialNames.size === 0) {
    return { detectedCreators: [], creatorsToCreate: [], socialHandles: {} };
  }

  // Search for each potential name in the database
  for (const potentialName of potentialNames) {
    const nameLower = potentialName.toLowerCase();
    console.log('Searching for:', potentialName);

    // Search for matching creator in database
    const creator = authors.find(a => {
      // Remove both spaces AND punctuation for comparison
      const creatorName = a.name.toLowerCase().replace(/[\s.]+/g, '');
      const creatorNameWithSpaces = a.name.toLowerCase();
      const channelHandle = a.channel ?
        a.channel.toLowerCase().split('@').pop().split('/')[0] : '';

      // Check various matching strategies
      const nameToCheck = nameLower.replace(/[\s.]+/g, '');

      // Exact matches or close contains (but must be substantial overlap)
      const exactMatch = creatorName === nameToCheck || creatorNameWithSpaces === nameLower;
      const channelMatch = channelHandle && (channelHandle === nameToCheck || nameToCheck === channelHandle);

      // Only allow "contains" if the match is substantial (at least 60% of the shorter string)
      const nameContains = creatorName.includes(nameToCheck) && nameToCheck.length >= creatorName.length * 0.6;
      const checkContains = nameToCheck.includes(creatorName) && creatorName.length >= nameToCheck.length * 0.6;
      const handleContains = channelHandle && (
        (channelHandle.includes(nameToCheck) && nameToCheck.length >= channelHandle.length * 0.6) ||
        (nameToCheck.includes(channelHandle) && channelHandle.length >= nameToCheck.length * 0.6)
      );

      return exactMatch || channelMatch || nameContains || checkContains || handleContains;
    });

    if (creator) {
      console.log('✅ Found existing creator:', creator.name);
      if (!detectedCreators.includes(creator.id)) {
        detectedCreators.push(creator.id);
      }
    } else {
      // Only suggest creating if it looks like a proper name
      // (has letters, not too short, not common words)
      const commonWords = ['video', 'collab', 'collaboration', 'challenge', 'reaction', 'review', 'episode', 'part', 'new', 'interviews', 'are', 'fascinating'];
      if (potentialName.length > 2 && !commonWords.includes(nameLower)) {
        console.log('❌ Creator not found, will suggest creating:', potentialName);
        // Store both name and URL/handle
        const urlOrHandle = socialHandles.get(potentialName);
        creatorsToCreate.push({
          name: potentialName,
          url: urlOrHandle || ''
        });
      }
    }
  }
  
  console.log('Detected existing creator IDs:', detectedCreators);
  console.log('Creators to create:', creatorsToCreate);

  return { detectedCreators, creatorsToCreate };
};
const fetchCreatorInfoFromYouTube = async () => {
  // Check if any social link has a YouTube URL
  const youtubeLink = newAuthor.socialLinks.find(link =>
    link.url && (link.url.includes('youtube.com') || link.url.includes('youtu.be'))
  );

  const instagramLink = newAuthor.socialLinks.find(link =>
    link.url && link.url.includes('instagram.com')
  );

  if (!youtubeLink && !instagramLink) {
    alert('Please enter a YouTube or Instagram URL in one of the social links first');
    return;
  }

  try {
    let creatorInfo = null;
    let updatedLinks = newAuthor.socialLinks;
    let source = '';

    // Try YouTube first if available
    if (youtubeLink && youtubeLink.url.trim()) {
      const ytInfo = await fetchYouTubeChannelInfo(youtubeLink.url);

      if (ytInfo) {
        creatorInfo = ytInfo;
        source = 'YouTube';
        // Update the YouTube link to the proper channel URL
        updatedLinks = newAuthor.socialLinks.map(link =>
          link === youtubeLink ? { ...link, url: ytInfo.channelUrl, platform: 'YouTube' } : link
        );
      }
    }

    // If YouTube didn't work or no photo, try Instagram as fallback
    if ((!creatorInfo || !creatorInfo.photo) && instagramLink && instagramLink.url.trim()) {
      const igInfo = await fetchInstagramProfilePic(instagramLink.url);

      if (igInfo) {
        // Use Instagram photo if we don't have one from YouTube
        if (!creatorInfo) {
          creatorInfo = igInfo;
          source = 'Instagram';
        } else if (!creatorInfo.photo && igInfo.photo) {
          creatorInfo.photo = igInfo.photo;
          source = 'YouTube & Instagram';
        }
      }
    }

    if (creatorInfo) {
      setNewAuthor({
        ...newAuthor,
        name: creatorInfo.name || newAuthor.name,
        photo: creatorInfo.photo || newAuthor.photo,
        socialLinks: updatedLinks
      });

      alert(`✓ Creator info fetched from ${source}!`);
    } else {
      alert('Could not fetch info from YouTube or Instagram. Please check the URLs or try again later.');
    }
  } catch (error) {
    console.error('Error fetching creator info:', error);
    alert('Failed to fetch creator info');
  }
};

const addAuthor = async () => {
  if (newAuthor.name.trim()) {
    let photoUrl = newAuthor.photo.trim();
    let creatorName = newAuthor.name.trim();

    // Filter out empty social links
    const validSocialLinks = newAuthor.socialLinks.filter(link => link.url && link.url.trim());

    // Fallback to generated avatar if no photo found
    if (!photoUrl) {
      photoUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${creatorName}`;
    }

    if (!creatorName) {
      alert('Please provide a creator name');
      return;
    }

    // CHECK FOR DUPLICATES
    const existingCreator = authors.find(a =>
      a.name.toLowerCase() === creatorName.toLowerCase()
    );

    if (existingCreator) {
      alert(`Creator "${existingCreator.name}" already exists in the database!`);
      setNewAuthor({ name: '', channel: '', photo: '', socialLinks: [{ platform: '', url: '' }] });
      return;
    }

    try {
      const response = await fetch('/api/creators', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: creatorName,
          channel: validSocialLinks.length > 0 ? validSocialLinks[0].url : '',
          photo: photoUrl,
          socialLinks: validSocialLinks
        })
      });

      const creator = await response.json();
      setAuthors([...authors, creator]);
      setNewAuthor({ name: '', channel: '', photo: '', socialLinks: [{ platform: '', url: '' }] });
    } catch (error) {
      console.error('Error adding creator:', error);
      alert('Error adding creator');
    }
  }
};

  const updateAuthor = async () => {
    if (editingAuthor && editingAuthor.name.trim()) {
      try {
        // Filter out empty social links
        const validSocialLinks = (editingAuthor.socialLinks || []).filter(link => link.url && link.url.trim());

        const response = await fetch('/api/creators', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...editingAuthor,
            socialLinks: validSocialLinks,
            channel: validSocialLinks.length > 0 ? validSocialLinks[0].url : ''
          })
        });

        const updated = await response.json();
        setAuthors(authors.map(a => a.id === updated.id ? updated : a));
        setEditingAuthor(null);
      } catch (error) {
        console.error('Error updating creator:', error);
        alert('Error updating creator');
      }
    }
  };

  const startEditingAuthor = (author) => {
    // Ensure socialLinks is initialized
    const socialLinks = getSocialLinks(author);
    setEditingAuthor({
      ...author,
      socialLinks: socialLinks.length > 0 ? socialLinks : [{ platform: '', url: '' }]
    });
  };

  const cancelEditingAuthor = () => {
    setEditingAuthor(null);
  };

  const deleteAuthor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this creator?')) return;
    
    try {
      await fetch(`/api/creators?id=${id}`, { method: 'DELETE' });
      setAuthors(authors.filter(a => a.id !== id));
      setVideos(videos.map(v => ({ 
        ...v, 
        authorIds: v.authorIds.filter(aid => aid !== id) 
      })));
      if (selectedAuthor?.id === id) {
        setSelectedAuthor(null);
      }
    } catch (error) {
      console.error('Error deleting creator:', error);
      alert('Error deleting creator');
    }
  };

 const addVideo = async () => {
  if (newVideo.title.trim() && newVideo.authorIds.length > 0) {
    // Detect collaborators from title
    const { detectedCreators, creatorsToCreate } = await detectCollaboratorsFromTitle(newVideo.title);
    
    let allAuthorIds = [...newVideo.authorIds];
    
    // Handle creators that need to be created
    if (creatorsToCreate.length > 0) {
      const creatorList = creatorsToCreate.join(', @');
      const shouldCreate = window.confirm(
        `Found @mentions in title: @${creatorList}\n\nThese creators don't exist yet. Create them and add as collaborators?`
      );
      
      if (shouldCreate) {
        for (const creatorHandle of creatorsToCreate) {
          try {
            // Try to fetch from YouTube
            const ytInfo = await fetchYouTubeChannelInfo(creatorHandle);
            
            const photoUrl = ytInfo?.photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${creatorHandle}`;
            const finalName = ytInfo?.name || creatorHandle;
            const channelUrl = ytInfo?.channelUrl || '';
            
            const response = await fetch('/api/creators', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                name: finalName, 
                channel: channelUrl, 
                photo: photoUrl 
              })
            });
            
            const newCreator = await response.json();
            setAuthors(prev => [...prev, newCreator]);
            allAuthorIds.push(newCreator.id);
          } catch (error) {
            console.error('Error creating creator:', error);
          }
        }
      }
    }
    
    // Add existing detected creators (FIXED: was detectedCollabs, should be detectedCreators)
    if (detectedCreators.length > 0) {
      const newDetected = detectedCreators.filter(id => !allAuthorIds.includes(id));
      if (newDetected.length > 0) {
        const detectedNames = newDetected.map(id => getAuthorName(id)).join(', ');
        const shouldAdd = window.confirm(
          `Also detected these existing creators: ${detectedNames}\n\nAdd them too?`
        );
        
        if (shouldAdd) {
          allAuthorIds = [...allAuthorIds, ...newDetected];
        }
      }
    }
    
    // Remove duplicates
    allAuthorIds = [...new Set(allAuthorIds)];
    
    try {
      const response = await fetch('/api/videos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newVideo, authorIds: allAuthorIds })
      });
      
      const video = await response.json();
      setVideos([...videos, video]);
      setNewVideo({ title: '', url: '', date: '', authorIds: [] });
      
      if (creatorsToCreate.length > 0 || detectedCreators.length > 0) {
        alert('Video added with detected collaborators!');
      }
    } catch (error) {
      console.error('Error adding video:', error);
      alert('Error adding video');
    }
  }
};

  const updateVideo = async () => {
    if (editingVideo && editingVideo.title.trim() && editingVideo.authorIds.length > 0) {
      try {
        const response = await fetch('/api/videos', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingVideo)
        });
        
        const updated = await response.json();
        setVideos(videos.map(v => v.id === updated.id ? updated : v));
        setEditingVideo(null);
      } catch (error) {
        console.error('Error updating video:', error);
        alert('Error updating video');
      }
    }
  };

  const startEditingVideo = (video) => {
    setEditingVideo({ ...video });
  };

  const cancelEditingVideo = () => {
    setEditingVideo(null);
  };

 const addCreatorInline = async () => {
  if (newCreatorName.trim()) {
    let photoUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${newCreatorName}`;
    
    // Try to fetch from YouTube if it looks like a channel
    const ytInfo = await fetchYouTubeChannelInfo(newCreatorName);
    if (ytInfo) {
      photoUrl = ytInfo.photo;
    }
    
    try {
      const response = await fetch('/api/creators', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: ytInfo?.name || newCreatorName, 
          channel: ytInfo?.channelUrl || '',
          photo: photoUrl
        })
      });
      
      const creator = await response.json();
      setAuthors([...authors, creator]);
      setNewVideo({ ...newVideo, authorIds: [...newVideo.authorIds, creator.id] });
      setNewCreatorName('');
      setShowAddCreatorInline(false);
    } catch (error) {
      console.error('Error adding creator:', error);
      alert('Error adding creator');
    }
  }
};

  const deleteVideo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await fetch(`/api/videos?id=${id}`, { method: 'DELETE' });
      setVideos(videos.filter(v => v.id !== id));
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Error deleting video');
    }
  };


  const getAuthorName = (id) => {
    const author = authors.find(a => a.id === id);
    return author ? author.name : 'Unknown';
  };

  const getAuthorVideos = (authorId) => {
    return videos.filter(v => v.authorIds.includes(authorId));
  };

  const getFilteredAuthors = () => {
    if (!searchQuery.trim()) return authors;
    const query = searchQuery.toLowerCase();
    return authors.filter(author => 
      author.name.toLowerCase().includes(query) || 
      (author.channel && author.channel.toLowerCase().includes(query))
    );
  };

  const viewAuthorPage = (author, withId = null) => {
    setSelectedAuthor(author);
    setPairWith(withId);
    setActiveTab('author-page');
    window.scrollTo({ top: 0 });
  };

  const getCollaborationStats = () => {
    const authorStats = authors.map(author => {
      const authorVideos = videos.filter(v => v.authorIds.includes(author.id));
      const soloVideos = authorVideos.filter(v => v.authorIds.length === 1).length;
      const collabVideos = authorVideos.filter(v => v.authorIds.length > 1).length;
      const collaborators = new Set();
      
      authorVideos.forEach(video => {
        video.authorIds.forEach(id => {
          if (id !== author.id) collaborators.add(id);
        });
      });

      return {
        author,
        totalVideos: authorVideos.length,
        soloVideos,
        collabVideos,
        uniqueCollaborators: collaborators.size
      };
    });

    return authorStats.filter(stat => stat.totalVideos > 0);
  };

  const getCollaborators = (authorId) => {
    const collaboratorIds = new Set();
    videos.forEach(video => {
      if (video.authorIds.includes(authorId) && video.authorIds.length > 1) {
        video.authorIds.forEach(id => {
          if (id !== authorId) collaboratorIds.add(id);
        });
      }
    });
    return Array.from(collaboratorIds).map(id => authors.find(a => a.id === id)).filter(Boolean);
  };

  // The same people, but with how often they appear together and sorted by it.
  // The page used to render an unordered wrap of pills, so somebody with forty
  // collaborators got forty identical chips in arbitrary order, and the one
  // they actually work with was no easier to find than the one they met once.
  const getCollaboratorCounts = (authorId) => {
    const counts = new Map();
    for (const video of videos) {
      if (!video.authorIds.includes(authorId) || video.authorIds.length < 2) continue;
      for (const id of video.authorIds) {
        if (id !== authorId) counts.set(id, (counts.get(id) || 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([id, count]) => ({ creator: authors.find((a) => a.id === id), count }))
      .filter((r) => r.creator)
      .sort((a, b) => b.count - a.count || a.creator.name.localeCompare(b.creator.name));
  };

  // Videos featuring both of two people, for the pair view.
  const getSharedVideos = (aId, bId) =>
    videos.filter((v) => v.authorIds.includes(aId) && v.authorIds.includes(bId));

  /*
   * Public video search.
   *
   * Creators are ANDed, not ORed. Adding two people should narrow the list to
   * the videos they made together, which is the question this app exists to
   * answer; ORing them would return everything either has ever been in, which
   * is just a longer list.
   */
  /*
   * Every video in a playlist, not just the newest fifty.
   *
   * playlistItems caps a page at 50 and hands back a nextPageToken. The bulk
   * import ignored it, so a scan could never see past the 50 most recent
   * uploads. A channel posting weekly produces 52 in a year, which is exactly
   * the case here after a year without a refresh: the scan would have looked
   * like it worked and quietly missed anything older.
   *
   * Each page costs one quota unit, so even a 200-video channel is four units
   * against a daily 10,000. The cap is there to stop a runaway loop, not to
   * save quota.
   */
  const fetchAllPlaylistItems = async (playlistId, { maxPages = 20, publishedAfter = '' } = {}) => {
    const ids = [];
    let pageToken;
    let reachedOlder = false;

    for (let page = 0; page < maxPages && !reachedOlder; page += 1) {
      const data = await youtubeApi('playlistItems', {
        part: 'snippet',
        maxResults: 50,
        playlistId,
        ...(pageToken ? { pageToken } : {}),
      });

      for (const item of data.items || []) {
        const id = item.snippet?.resourceId?.videoId;
        if (!id) continue;
        // Uploads come back newest first, so a date is the natural stopping
        // point. Stopping at the first id we already hold would be wrong: a
        // curated database has gaps, and something published between two
        // videos we kept is still something we never had.
        if (publishedAfter && (item.snippet?.publishedAt || '').slice(0, 10) <= publishedAfter) {
          reachedOlder = true;
          continue;
        }
        ids.push(id);
      }

      pageToken = data.nextPageToken;
      if (!pageToken) break;
    }
    return ids;
  };

  /*
   * Check every channel already in the database for uploads we do not have.
   *
   * Answering "what has been posted since we last looked" used to mean pasting
   * each channel URL in turn and reading a list of two hundred videos, nearly
   * all of them already imported. The videos table records channelId now, so
   * the set of channels to check is already known and does not have to be
   * typed.
   *
   * Cheap: one batched call to turn channel ids into uploads playlists, then
   * one page per channel in the common case where little has changed. Around
   * twenty quota units against a daily 10,000.
   */
  const checkTrackedChannelsForNew = async () => {
    setIsLoadingBulk(true);
    setFetchedVideos([]);
    setBulkScanSummary('');
    try {
      const byChannel = new Map();
      for (const v of videos) {
        if (!v.channelId) continue;
        const c = byChannel.get(v.channelId) || { title: v.channelTitle, count: 0, newest: '' };
        c.count += 1;
        if ((v.date || '') > c.newest) c.newest = v.date || '';
        byChannel.set(v.channelId, c);
      }

      // Which channels are worth checking, and why, lives in
      // src/lib/channelPrefs.js so this and the script cannot disagree.
      const { kept, skippedByName } = trackedChannels(byChannel);
      const channelIds = kept.map(([id]) => id);

      if (channelIds.length === 0) {
        alert(
          byChannel.size === 0
            ? 'No channels are recorded against your videos yet. Run scripts/backfill-channels.js first.'
            : 'No channel here has enough videos to count as one you follow.'
        );
        return;
      }

      const known = new Set(videos.map((v) => youtubeIdOf(v.url)).filter(Boolean));

      // Channel id to uploads playlist, 50 channels per call.
      const uploads = [];
      for (let i = 0; i < channelIds.length; i += 50) {
        const data = await youtubeApi('channels', {
          part: 'contentDetails',
          id: channelIds.slice(i, i + 50).join(','),
        });
        for (const item of data.items || []) {
          const list = item.contentDetails?.relatedPlaylists?.uploads;
          if (list) {
            uploads.push({
              channelId: item.id,
              playlistId: list,
              since: byChannel.get(item.id)?.newest || '',
            });
          }
        }
      }

      const newIds = [];
      let checked = 0;
      for (const { playlistId, since } of uploads) {
        try {
          const ids = await fetchAllPlaylistItems(playlistId, { maxPages: 10, publishedAfter: since });
          for (const id of ids) if (!known.has(id)) newIds.push(id);
          checked += 1;
        } catch (err) {
          if (isYoutubeOutage(err)) {
            setYoutubeNotice(`${err.message} Checked ${checked} of ${uploads.length} channels.`);
            break;
          }
          console.warn('Could not check a channel:', err);
        }
      }

      if (newIds.length === 0) {
        setBulkScanSummary(
          `Checked ${checked} channel(s). Nothing posted since the newest video you already have.` +
            (skippedByName.length ? ` Skipped ${skippedByName.join(", ")}.` : "")
        );
        return;
      }

      const items = [];
      for (let i = 0; i < newIds.length; i += 50) {
        const data = await youtubeApi('videos', {
          part: 'snippet',
          id: newIds.slice(i, i + 50).join(','),
        });
        items.push(...(data.items || []));
      }

      const fetched = items.map((item) => {
        const snippet = item.snippet || {};
        const thumbs = snippet.thumbnails || {};
        return {
          id: item.id,
          title: snippet.title || '(untitled)',
          url: `https://www.youtube.com/watch?v=${item.id}`,
          publishedAt: snippet.publishedAt ? snippet.publishedAt.split('T')[0] : '',
          thumbnail:
            thumbs.medium?.url ||
            thumbs.high?.url ||
            thumbs.default?.url ||
            `https://i.ytimg.com/vi/${item.id}/hqdefault.jpg`,
          channelTitle: snippet.channelTitle || '',
          channelId: snippet.channelId || '',
          // Kept so the collaboration filter can read it without a second
          // round trip; part=snippet already returns it.
          description: snippet.description || '',
          alreadyHave: false,
        };
      });
      fetched.sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''));

      setBulkScanSummary(
        `Checked ${checked} channel(s): ${fetched.length} video(s) posted since the newest you have, newest first.` +
          (skippedByName.length ? ` Skipped ${skippedByName.join(", ")}.` : "")
      );
      setFetchedVideos(fetched);
      setSelectedVideos(new Set(fetched.map((v) => v.id)));
    } catch (error) {
      console.error('Channel check failed:', error);
      alert(`Could not check channels: ${error.message}`);
    } finally {
      setIsLoadingBulk(false);
    }
  };

  /*
   * Does this scan result mention somebody already in the database?
   *
   * A channel posts far more than it collaborates on, so most of a scan is
   * solo videos this app has no reason to hold. There is no field saying "this
   * is a collaboration", and the description is not fetched until import, so
   * the title is what there is to go on: it names a tracked creator, or a
   * handle belonging to one.
   *
   * Deliberately loose. It is a filter for narrowing a list by hand, not a
   * classifier, and hiding a real collaboration is worse than showing a solo
   * video that can be left unticked.
   */
  const trackedNameNeedles = useMemo(() => {
    // Per creator, so the uploader can be excluded from their own videos.
    return authors.map((a) => {
      const needles = [];
      const name = String(a.name || '').trim();
      // Four characters, so a short name cannot match half the library.
      if (name.length >= 4) needles.push(name.toLowerCase());
      for (const l of a.socialLinks || []) {
        const m = String(l.url || '').match(/\/@?([A-Za-z0-9._-]+)\/?$/);
        if (m && m[1].length >= 4) needles.push(m[1].toLowerCase());
      }
      return { key: name.toLowerCase().replace(/[^a-z0-9]/g, ''), needles };
    });
  }, [authors]);

  const mentionsTrackedCreator = (video) => {
    const hay = `${video.title || ''}\n${video.description || ''}`.toLowerCase();
    const uploader = String(video.channelTitle || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return trackedNameNeedles.some(
      // Skip the uploader. A description always links its own channel, so
      // counting that matched 93% of everything and filtered nothing. Ignoring
      // it, the question becomes "does this name somebody OTHER than the person
      // who posted it", which is as close to "is this a collaboration" as a
      // title and description can get.
      ({ key, needles }) => key !== uploader && needles.some((n) => hay.includes(n))
    );
  };

  const visibleFetchedVideos = useMemo(
    () => (onlyLikelyCollabs ? fetchedVideos.filter(mentionsTrackedCreator) : fetchedVideos),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchedVideos, onlyLikelyCollabs, trackedNameNeedles]
  );

  // Scan results grouped by the channel that published them, biggest first.
  const videosByChannel = useMemo(() => {
    const groups = new Map();
    for (const v of visibleFetchedVideos) {
      const key = v.channelTitle || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(v);
    }
    return [...groups.entries()]
      .map(([channel, items]) => ({ channel, items }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [visibleFetchedVideos]);

  const searchedVideos = useMemo(() => {
    const needle = videoFilterText.trim().toLowerCase();
    return videos
      .filter((v) => videoFilterCreators.every((id) => v.authorIds.includes(id)))
      .filter(
        (v) =>
          !needle ||
          (v.title || '').toLowerCase().includes(needle) ||
          (v.channelTitle || '').toLowerCase().includes(needle)
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [videos, videoFilterCreators, videoFilterText]);

  // Only offer creators who could actually narrow the result further, so the
  // picker stops suggesting people who share no videos with the current set.
  const creatorSuggestions = useMemo(() => {
    const typed = creatorPickerText.trim().toLowerCase();
    const possible = new Set();
    for (const v of searchedVideos) for (const id of v.authorIds) possible.add(id);
    return authors
      .filter((a) => !videoFilterCreators.includes(a.id))
      .filter((a) => possible.has(a.id))
      .filter((a) => !typed || a.name.toLowerCase().includes(typed))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [authors, searchedVideos, videoFilterCreators, creatorPickerText]);

  const getLeaderboardData = () => {
    const authorStats = authors.map(author => {
      const authorVideos = videos.filter(v => v.authorIds.includes(author.id));
      const collabVideos = authorVideos.filter(v => v.authorIds.length > 1);
      const collaborators = new Set();
      
      authorVideos.forEach(video => {
        if (video.authorIds.length > 1) {
          video.authorIds.forEach(id => {
            if (id !== author.id) collaborators.add(id);
          });
        }
      });

      return {
        author,
        collabCount: collabVideos.length,
        collaboratorCount: collaborators.size
      };
    });

    const mostCollabs = [...authorStats]
      .filter(stat => stat.collabCount > 0)
      .sort((a, b) => b.collabCount - a.collabCount)
      .slice(0, 10);

    const mostCollaborators = [...authorStats]
      .filter(stat => stat.collaboratorCount > 0)
      .sort((a, b) => b.collaboratorCount - a.collaboratorCount)
      .slice(0, 10);

    return { mostCollabs, mostCollaborators };
  };

  const submitSuggestion = () => {
    if (newSuggestion.url.trim()) {
      setSuggestedVideos([...suggestedVideos, {
        id: Date.now(),
        url: newSuggestion.url,
        status: 'pending',
        submittedAt: new Date().toISOString()
      }]);
      setNewSuggestion({ url: '' });
      setShowSuggestionForm(false);
      setSuggestionSubmitted(true);

      // Hide the success message after 5 seconds
      setTimeout(() => {
        setSuggestionSubmitted(false);
      }, 5000);
    }
  };

  const approveSuggestion = async (suggestion) => {
    // Pre-populate the add video form with the suggested URL
    setNewVideo({ title: '', url: suggestion.url, date: '', authorIds: [] });

    // Switch to the videos (add video) tab
    setActiveTab('videos');

    // Remove the suggestion from the queue
    setSuggestedVideos(suggestedVideos.filter(s => s.id !== suggestion.id));
  };

  const denySuggestion = (suggestionId) => {
    setSuggestedVideos(suggestedVideos.filter(s => s.id !== suggestionId));
  };

  // Find duplicate creators based on name similarity
  const findDuplicateCreators = () => {
    const duplicates = [];
    const seen = new Map();

    authors.forEach(author => {
      const normalizedName = author.name.toLowerCase().trim().replace(/\s+/g, ' ');

      if (seen.has(normalizedName)) {
        const existing = seen.get(normalizedName);
        const existingDupe = duplicates.find(d => d.includes(existing));

        if (existingDupe) {
          existingDupe.push(author);
        } else {
          duplicates.push([existing, author]);
        }
      } else {
        seen.set(normalizedName, author);
      }
    });

    return duplicates;
  };

  // Bulk delete selected creators
  const bulkDeleteCreators = async () => {
    if (selectedCreators.size === 0) {
      alert('Please select creators to delete');
      return;
    }

    const count = selectedCreators.size;
    if (!window.confirm(`Are you sure you want to delete ${count} creator(s)? This will also remove them from all videos.`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const creatorId of selectedCreators) {
      try {
        await fetch(`/api/creators/${creatorId}`, {
          method: 'DELETE'
        });
        successCount++;
      } catch (error) {
        console.error('Error deleting creator:', creatorId, error);
        failCount++;
      }
    }

    // Refresh data
    const response = await fetch('/api/creators');
    const creatorsData = await response.json();
    setAuthors(creatorsData);

    const videosResponse = await fetch('/api/videos');
    const videosData = await videosResponse.json();
    setVideos(videosData);

    setSelectedCreators(new Set());
    alert(`Deleted ${successCount} creator(s).${failCount > 0 ? ` ${failCount} failed.` : ''}`);
  };

  // Merge duplicate creators
  const mergeCreators = async (creatorGroup) => {
    if (creatorGroup.length < 2) {
      alert('Need at least 2 creators to merge');
      return;
    }

    // Ask user to select which one should be the primary creator
    const options = creatorGroup.map((creator, idx) =>
      `${idx + 1}. ID ${creator.id}: ${creator.name} (${videos.filter(v => v.authorIds.includes(creator.id)).length} videos)`
    ).join('\n');

    const choice = prompt(
      `Select the primary creator to keep (enter 1-${creatorGroup.length}):\n\n${options}\n\nAll other creators will be merged into this one.`
    );

    if (!choice) return; // User cancelled

    const choiceNum = parseInt(choice);
    if (isNaN(choiceNum) || choiceNum < 1 || choiceNum > creatorGroup.length) {
      alert('Invalid selection');
      return;
    }

    const primaryCreator = creatorGroup[choiceNum - 1];
    const mergeCreators = creatorGroup.filter((c, idx) => idx !== choiceNum - 1);

    const confirmMsg = `Merge these creators into "${primaryCreator.name}"?\n\n` +
      mergeCreators.map(c => `• ${c.name} (ID ${c.id})`).join('\n') +
      `\n\nThis will:\n- Transfer all videos to "${primaryCreator.name}"\n- Combine social links\n- Delete the duplicate creators`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const response = await fetch('/api/creators', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primaryId: primaryCreator.id,
          mergeIds: mergeCreators.map(c => c.id)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to merge creators');
      }

      const result = await response.json();

      // Refresh data
      const creatorsResponse = await fetch('/api/creators');
      const creatorsData = await creatorsResponse.json();
      setAuthors(creatorsData);

      const videosResponse = await fetch('/api/videos');
      const videosData = await videosResponse.json();
      setVideos(videosData);

      alert(result.message || 'Creators merged successfully!');
    } catch (error) {
      console.error('Error merging creators:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Refresh all creator profile pictures
  const refreshAllCreatorPhotos = async () => {
    // Check creators with YouTube or Instagram links
    const creatorsWithSocial = authors.filter(author =>
      author.socialLinks && author.socialLinks.some(link =>
        link.url && (
          link.url.includes('youtube.com') ||
          link.url.includes('youtu.be') ||
          link.url.includes('instagram.com')
        )
      )
    );

    if (creatorsWithSocial.length === 0) {
      alert('No creators with YouTube or Instagram links found.');
      return;
    }

    if (!window.confirm(`Refresh profile pictures for ${creatorsWithSocial.length} creator(s)? This may take a while.`)) {
      return;
    }

    // These used to be three counters, and "skipped" covered both "the photo
    // was already right" and "we could not get a photo at all". Every
    // Instagram-only creator therefore reported as already up to date, which
    // is how a feature that fetches nothing looks like a feature that works.
    let updatedCount = 0;
    let unchangedCount = 0;
    let noPhotoCount = 0;
    let failedCount = 0;
    let stoppedEarly = null;

    for (const creator of creatorsWithSocial) {
      try {
        let newPhoto = null;

        console.log(`🔄 Checking ${creator.name}...`);

        // Try YouTube first if available
        const youtubeLink = creator.socialLinks.find(link =>
          link.url && (link.url.includes('youtube.com') || link.url.includes('youtu.be'))
        );

        if (youtubeLink) {
          try {
            const ytInfo = await fetchYouTubeChannelInfo(youtubeLink.url);
            if (ytInfo && ytInfo.photo) newPhoto = ytInfo.photo;
          } catch (err) {
            // Grinding through every remaining creator only to fail each one
            // wastes time and, on a quota error, tells you nothing new.
            if (isYoutubeOutage(err)) {
              stoppedEarly = err.message;
              break;
            }
            throw err;
          }
        }

        // If no YouTube photo, try Instagram
        if (!newPhoto) {
          const instagramLink = creator.socialLinks.find(link =>
            link.url && link.url.includes('instagram.com')
          );

          if (instagramLink) {
            // This cannot return a photo any more. Instagram stopped exposing
            // profile pictures to unauthenticated requests; see the header of
            // api/instagram.js for what was tested. Kept so that the day a
            // provider is added, this path starts working on its own.
            const igInfo = await fetchInstagramProfilePic(instagramLink.url);
            if (igInfo && igInfo.photo) newPhoto = igInfo.photo;
          }
        }

        if (!newPhoto) {
          noPhotoCount += 1;
          continue;
        }

        if (newPhoto === creator.photo) {
          unchangedCount += 1;
          continue;
        }

        {
          // Update creator with new photo
          const response = await fetch('/api/creators', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: creator.id,
              name: creator.name,
              channel: creator.channel,
              photo: newPhoto,
              socialLinks: creator.socialLinks
            })
          });

          if (response.ok) {
            updatedCount += 1;
          } else if (response.status === 401) {
            // Writes need a session now. Without this the run would carry on
            // being refused for every creator and still report success.
            stoppedEarly = 'Your admin session expired. Log in again to continue.';
            break;
          } else {
            failedCount += 1;
          }
        }
      } catch (error) {
        console.error(`Error updating ${creator.name}:`, error);
        failedCount += 1;
      }
    }

    // Refresh data
    const response = await fetch('/api/creators');
    const creatorsData = await response.json();
    setAuthors(creatorsData);

    // Report the outcomes separately. "No photo available" is the honest
    // result for a creator with only an Instagram link, and calling it
    // "up to date" hid the fact that nothing had been fetched.
    const lines = [`Updated ${updatedCount} profile picture(s).`];
    if (unchangedCount > 0) lines.push(`${unchangedCount} already up to date.`);
    if (noPhotoCount > 0) {
      lines.push(
        `${noPhotoCount} had no photo available. Only YouTube links can supply one; ` +
          `Instagram no longer allows it, so those need a photo added by hand.`
      );
    }
    if (failedCount > 0) lines.push(`${failedCount} failed, see the console for details.`);
    if (stoppedEarly) lines.push(`\nStopped early: ${stoppedEarly}`);
    alert(lines.join(' '));
  };

  // Rescan single video for collaborators
  const rescanSingleVideo = async (video) => {
    if (!window.confirm(`Rescan "${video.title}" for collaborators?`)) {
      return;
    }

    try {
      // Fetch video details from YouTube to get description
      const videoId = video.url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&\n?#]+)/)?.[1];

      if (!videoId) {
        alert('Could not extract video ID from URL');
        return;
      }

      const data = await youtubeApi('videos', { part: 'snippet', id: videoId });

      if (!data.items || data.items.length === 0) {
        alert('No video data found. The video may be private, deleted, or the URL may be wrong.');
        return;
      }

      const snippet = data.items[0].snippet;
      const videoDescription = snippet.description;
      const channelTitle = snippet.channelTitle;

      // Detect collaborators from title and description
      const { detectedCreators, creatorsToCreate } = await detectCollaboratorsFromTitle(
        video.title,
        videoDescription,
        channelTitle
      );

      // Create any new creators
      for (const creatorData of creatorsToCreate) {
        try {
          // Check if creator already exists (by name or social handle)
          const normalizedName = creatorData.name.toLowerCase().replace(/[\s.]+/g, '');

          const existingCreator = authors.find(a => {
            // Check by name
            const existingNormalizedName = a.name.toLowerCase().replace(/[\s.]+/g, '');
            if (existingNormalizedName === normalizedName) return true;

            // Check by social handle if provided
            if (creatorData.url && a.socialLinks && a.socialLinks.length > 0) {
              const urlToCheck = creatorData.url.toLowerCase();
              return a.socialLinks.some(link => {
                if (!link.url) return false;
                const linkUrl = link.url.toLowerCase();

                // Extract handle from URL - ignore if empty or just slashes
                const existingHandle = linkUrl.split('/').pop();
                if (!existingHandle || existingHandle.length === 0) return false;

                const newHandle = urlToCheck.replace(/^@/, '');
                if (!newHandle || newHandle.length === 0) return false;

                // Check if URLs match or handles match
                return linkUrl === urlToCheck ||
                       linkUrl.includes(newHandle) ||
                       urlToCheck.includes(existingHandle);
              });
            }

            return false;
          });

          if (existingCreator) {
            console.log(`⚠️ Creator "${creatorData.name}" already exists as "${existingCreator.name}" (ID: ${existingCreator.id}), skipping creation`);
            // Add the existing creator's ID to detected creators if not already there
            if (!detectedCreators.includes(existingCreator.id)) {
              detectedCreators.push(existingCreator.id);
            }
            continue;
          }

          const handleToUrl = (url) => {
            if (!url) return '';
            if (url.startsWith('http://') || url.startsWith('https://')) return url;
            const cleanHandle = url.replace(/^@/, '');
            return `https://www.instagram.com/${cleanHandle}`;
          };

          const fullUrl = handleToUrl(creatorData.url);

          const createResponse = await fetch('/api/creators', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: creatorData.name,
              channel: creatorData.channel || '',
              photo: creatorData.photo || '',
              socialLinks: creatorData.socialLinks || (fullUrl ? [{
                platform: creatorData.platform || 'Instagram',
                url: fullUrl
              }] : [])
            })
          });

          if (createResponse.ok) {
            const newCreator = await createResponse.json();
            detectedCreators.push(newCreator.id);
          }
        } catch (error) {
          console.error('Error creating creator:', creatorData.name, error);
        }
      }

      // Get current and new collaborators
      const currentCollaboratorIds = new Set(video.authorIds || []);
      const newCollaboratorIds = detectedCreators.filter(id => !currentCollaboratorIds.has(id));

      if (newCollaboratorIds.length > 0) {
        const allCollaboratorIds = [...currentCollaboratorIds, ...newCollaboratorIds];

        const updateResponse = await fetch(`/api/videos`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: video.id,
            title: video.title,
            url: video.url,
            date: video.date,
            authorIds: allCollaboratorIds
          })
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          throw new Error(errorData.error || 'Failed to update video');
        }

        // Refresh data
        const videosResponse = await fetch('/api/videos');
        const videosData = await videosResponse.json();
        setVideos(videosData);

        const creatorsResponse = await fetch('/api/creators');
        const creatorsData = await creatorsResponse.json();
        setAuthors(creatorsData);

        alert(`Added ${newCollaboratorIds.length} new collaborator(s) to this video!`);
      } else {
        alert('No new collaborators detected');
      }
    } catch (error) {
      console.error('Error rescanning video:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Bulk delete selected videos
  const bulkDeleteVideos = async () => {
    if (selectedVideoIds.size === 0) {
      alert('Please select videos to delete');
      return;
    }

    const count = selectedVideoIds.size;
    if (!window.confirm(`Are you sure you want to delete ${count} video(s)?`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const videoId of selectedVideoIds) {
      try {
        await fetch(`/api/videos/${videoId}`, {
          method: 'DELETE'
        });
        successCount++;
      } catch (error) {
        console.error('Error deleting video:', videoId, error);
        failCount++;
      }
    }

    // Refresh data
    const response = await fetch('/api/videos');
    const videosData = await response.json();
    setVideos(videosData);

    setSelectedVideoIds(new Set());
    alert(`Deleted ${successCount} video(s).${failCount > 0 ? ` ${failCount} failed.` : ''}`);
  };

  // Rescan selected videos for collaborators
  const rescanVideoCollaborators = async () => {
    if (selectedRescanVideos.size === 0) {
      alert('Please select videos to rescan');
      return;
    }

    const count = selectedRescanVideos.size;
    if (!window.confirm(`Rescan ${count} video(s) for potential collaborators? This will add any newly detected collaborators.`)) {
      return;
    }

    setIsRescanning(true);
    const videosToRescan = Array.from(selectedRescanVideos).map(id =>
      videos.find(v => v.id === id)
    ).filter(Boolean);

    setRescanProgress({ current: 0, total: videosToRescan.length });

    let addedCollaboratorsCount = 0;
    let newCreatorsCount = 0;

    for (let i = 0; i < videosToRescan.length; i++) {
      const video = videosToRescan[i];
      setRescanProgress({ current: i + 1, total: videosToRescan.length });

      try {
        // Fetch video details from YouTube to get description
        const videoId = video.url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&\n?#]+)/)?.[1];

        if (!videoId) {
          console.log('Could not extract video ID from:', video.url);
          continue;
        }

        let data;
        try {
          data = await youtubeApi('videos', { part: 'snippet', id: videoId });
        } catch (err) {
          // Quota exhaustion part-way through a bulk rescan used to be
          // invisible: every remaining video silently "had no data" and the
          // run reported success having done nothing. Stop and say so, so the
          // counts at the end mean something.
          if (isYoutubeOutage(err)) {
            alert(
              `Stopped after ${i} of ${videosToRescan.length} videos.\n\n${err.message}`
            );
            break;
          }
          console.error('Lookup failed for', videoId, err);
          continue;
        }

        if (!data.items || data.items.length === 0) {
          console.log('No video data found for:', videoId);
          continue;
        }

        const snippet = data.items[0].snippet;
        const videoDescription = snippet.description;
        const channelTitle = snippet.channelTitle;

        // Detect collaborators from title and description
        const { detectedCreators, creatorsToCreate } = await detectCollaboratorsFromTitle(
          video.title,
          videoDescription,
          channelTitle
        );

        // Get current collaborator IDs
        const currentCollaboratorIds = new Set(video.authorIds || []);

        // Create any new creators that were detected
        for (const creatorData of creatorsToCreate) {
          try {
            // Check if creator already exists (by name or social handle)
            const normalizedName = creatorData.name.toLowerCase().replace(/[\s.]+/g, '');

            const existingCreator = authors.find(a => {
              // Check by name
              const existingNormalizedName = a.name.toLowerCase().replace(/[\s.]+/g, '');
              if (existingNormalizedName === normalizedName) return true;

              // Check by social handle if provided
              if (creatorData.url && a.socialLinks && a.socialLinks.length > 0) {
                const urlToCheck = creatorData.url.toLowerCase();
                return a.socialLinks.some(link => {
                  if (!link.url) return false;
                  const linkUrl = link.url.toLowerCase();

                  // Extract handle from URL - ignore if empty or just slashes
                  const existingHandle = linkUrl.split('/').pop();
                  if (!existingHandle || existingHandle.length === 0) return false;

                  const newHandle = urlToCheck.replace(/^@/, '');
                  if (!newHandle || newHandle.length === 0) return false;

                  // Check if URLs match or handles match
                  return linkUrl === urlToCheck ||
                         linkUrl.includes(newHandle) ||
                         urlToCheck.includes(existingHandle);
                });
              }

              return false;
            });

            if (existingCreator) {
              console.log(`⚠️ Creator "${creatorData.name}" already exists as "${existingCreator.name}" (ID: ${existingCreator.id}), skipping creation`);
              // Add the existing creator's ID to detected creators if not already there
              if (!detectedCreators.includes(existingCreator.id)) {
                detectedCreators.push(existingCreator.id);
              }
              continue;
            }

            // Convert handle to full URL if needed
            const handleToUrl = (url) => {
              if (!url) return '';
              if (url.startsWith('http://') || url.startsWith('https://')) return url;
              const cleanHandle = url.replace(/^@/, '');
              return `https://www.instagram.com/${cleanHandle}`;
            };

            const fullUrl = handleToUrl(creatorData.url);

            const createResponse = await fetch('/api/creators', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: creatorData.name,
                channel: creatorData.channel || '',
                photo: creatorData.photo || '',
                socialLinks: creatorData.socialLinks || (fullUrl ? [{
                  platform: creatorData.platform || 'Instagram',
                  url: fullUrl
                }] : [])
              })
            });

            if (createResponse.ok) {
              const newCreator = await createResponse.json();
              detectedCreators.push(newCreator.id);  // Push the ID, not the full object
              newCreatorsCount++;

              // Add to local authors array to prevent duplicates in same rescan batch
              authors.push(newCreator);

              console.log(`✅ Created new creator: ${newCreator.name} (ID: ${newCreator.id})`);
            }
          } catch (error) {
            console.error('Error creating creator:', creatorData.name, error);
          }
        }

        // Find collaborators that aren't already on this video
        // Note: detectedCreators is already an array of IDs, not objects
        const newCollaboratorIds = detectedCreators
          .filter(id => !currentCollaboratorIds.has(id));

        if (newCollaboratorIds.length > 0) {
          // Update video with all collaborators (existing + new)
          const allCollaboratorIds = [...currentCollaboratorIds, ...newCollaboratorIds];

          const response = await fetch(`/api/videos`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: video.id,
              title: video.title,
              url: video.url,
              date: video.date,
              authorIds: allCollaboratorIds
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error updating video:', video.title, errorData);
            throw new Error(`Failed to update video: ${errorData.error}`);
          }

          addedCollaboratorsCount += newCollaboratorIds.length;
        }

      } catch (error) {
        console.error('Error rescanning video:', video.title, error);
      }
    }

    // Refresh data
    const videosResponse = await fetch('/api/videos');
    const videosData = await videosResponse.json();
    setVideos(videosData);

    const creatorsResponse = await fetch('/api/creators');
    const creatorsData = await creatorsResponse.json();
    setAuthors(creatorsData);

    setIsRescanning(false);
    setRescanProgress({ current: 0, total: 0 });
    setSelectedRescanVideos(new Set());

    alert(`Rescan complete!\n${addedCollaboratorsCount} collaborator(s) added to videos.\n${newCreatorsCount} new creator(s) created.`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
const fetchVideoInfo = async () => {
  if (!newVideo.url.trim()) {
    alert('Please enter a video URL first');
    return;
  }

  try {
    let videoId = null;
    const url = newVideo.url;

    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    }

    if (!videoId) {
      alert('Could not extract video ID from URL. Make sure it\'s a valid YouTube URL.');
      return;
    }

    // Fetch video details including description using YouTube Data API v3
    let videoDescription = '';
    let videoTitle = '';
    let channelName = '';
    let channelUrl = '';

    // The Data API gives us the description too, which is what collaborator
    // detection reads. oEmbed below can supply a title but never a description,
    // so a silent failure here degrades detection without saying anything.
    try {
      const apiData = await youtubeApi('videos', { part: 'snippet', id: videoId });
      if (apiData.items && apiData.items.length > 0) {
        const snippet = apiData.items[0].snippet;
        videoTitle = snippet.title;
        videoDescription = snippet.description;
        channelName = snippet.channelTitle;
        channelUrl = `https://www.youtube.com/channel/${snippet.channelId}`;
      }
    } catch (error) {
      setYoutubeNotice(
        isYoutubeOutage(error)
          ? `${error.message} Titles will still fill in, but collaborator detection needs the description and will be less accurate until this clears.`
          : ''
      );
      console.warn('YouTube Data API lookup failed:', error);
    }

    // oEmbed needs no key and no quota, so it still works when the Data API is
    // exhausted. It returns a title and channel, but no description.
    if (!videoTitle) {
      const response = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch video info');
      }

      const data = await response.json();
      videoTitle = data.title;
      channelName = data.author_name;
      channelUrl = data.author_url || '';
    }

    const data = { title: videoTitle, author_name: channelName, author_url: channelUrl };
    
    setNewVideo(prev => ({
      ...prev,
      title: data.title || prev.title
    }));

    const creatorName = data.author_name;
    
    const existingAuthor = authors.find(a => 
      a.name.toLowerCase() === creatorName.toLowerCase() ||
      (a.channel && a.channel.toLowerCase().includes(creatorName.toLowerCase()))
    );

    if (existingAuthor) {
      if (!newVideo.authorIds.includes(existingAuthor.id)) {
        setNewVideo(prev => ({
          ...prev,
          authorIds: [...prev.authorIds, existingAuthor.id]
        }));
      }

      // Check if creator has a YouTube link, if not, update them with the channel URL
      const hasYouTubeLink = existingAuthor.socialLinks && existingAuthor.socialLinks.some(link =>
        link.url && (link.url.includes('youtube.com') || link.url.includes('youtu.be'))
      );

      if (!hasYouTubeLink && channelUrl) {
        try {
          // Add YouTube link to existing creator's social links
          const updatedSocialLinks = existingAuthor.socialLinks || [];
          updatedSocialLinks.push({
            platform: 'YouTube',
            url: channelUrl
          });

          const updateResponse = await fetch('/api/creators', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: existingAuthor.id,
              name: existingAuthor.name,
              channel: channelUrl,
              photo: existingAuthor.photo,
              socialLinks: updatedSocialLinks
            })
          });

          if (updateResponse.ok) {
            const updatedCreator = await updateResponse.json();
            setAuthors(authors.map(a => a.id === updatedCreator.id ? updatedCreator : a));
            console.log(`✅ Added YouTube channel to ${existingAuthor.name}: ${channelUrl}`);
          }
        } catch (error) {
          console.error('Error updating creator with YouTube link:', error);
        }
      }

      alert(`Video info fetched! Creator "${creatorName}" already exists and has been selected.`);
    } else {
      const shouldCreate = window.confirm(
        `Creator "${creatorName}" not found in database. Would you like to create them?`
      );
      
      if (shouldCreate) {
        // Try to get channel info from YouTube first
        const ytInfo = await fetchYouTubeChannelInfo(creatorName);
        
        const photoUrl = ytInfo?.photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${creatorName}`;
        const finalName = ytInfo?.name || creatorName;
        const channelUrl = ytInfo?.channelUrl || data.author_url || '';
        
        const response = await fetch('/api/creators', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            name: finalName, 
            channel: channelUrl, 
            photo: photoUrl 
          })
        });
        
        const newCreator = await response.json();
        setAuthors(prev => [...prev, newCreator]);
        setNewVideo(prev => ({
          ...prev,
          authorIds: [...prev.authorIds, newCreator.id]
        }));
        alert('Video info fetched and new creator added with YouTube profile picture!');
      } else {
        alert('Video info fetched! Please select the creator(s) manually.');
      }
    }

    const today = new Date().toISOString().split('T')[0];
    setNewVideo(prev => ({
      ...prev,
      date: prev.date || today
    }));

    // Auto-detect collaborators from title and description
    // Pass the main channel name to exclude it from detection
    const { detectedCreators, creatorsToCreate } = await detectCollaboratorsFromTitle(
      data.title,
      videoDescription,
      creatorName
    );

    // Set pending collaborators for user to review
    if (detectedCreators.length > 0 || creatorsToCreate.length > 0) {
      setPendingCollaborators({
        existing: detectedCreators.map(id => ({
          id,
          name: getAuthorName(id),
          type: 'existing'
        })),
        toCreate: creatorsToCreate.map(creator => ({
          name: creator.name,
          url: creator.url,
          type: 'new'
        }))
      });
    }

  } catch (error) {
    console.error('Error fetching video info:', error);
    alert('Failed to fetch video info. Make sure it\'s a valid YouTube URL and the video is public.');
  }
};

// Fetch videos from a playlist or channel
const fetchBulkVideos = async () => {
  if (!bulkImportUrl.trim()) {
    alert('Please enter a YouTube channel or playlist URL');
    return;
  }

  setIsLoadingBulk(true);
  setFetchedVideos([]);
  setSelectedVideos(new Set());

  try {
    let videoIds = [];

    // Check if it's a playlist URL
    const playlistMatch = bulkImportUrl.match(/[?&]list=([^&]+)/);
    if (playlistMatch) {
      const playlistId = playlistMatch[1];

      videoIds = await fetchAllPlaylistItems(playlistId);
    } else {
      // Try to extract channel ID or username
      const channelMatch = bulkImportUrl.match(/youtube\.com\/(channel\/|@|c\/)?([^/?]+)/);
      if (channelMatch) {
        const channelIdentifier = channelMatch[2];

        // Get channel ID
        let channelId;
        if (bulkImportUrl.includes('/channel/')) {
          channelId = channelIdentifier;
        } else {
          // Look up by handle first, then by the older username form.
          const searchData = await youtubeApi('channels', {
            part: 'id',
            forHandle: channelIdentifier,
          });

          if (searchData.items && searchData.items.length > 0) {
            channelId = searchData.items[0].id;
          } else {
            const userData = await youtubeApi('channels', {
              part: 'id',
              forUsername: channelIdentifier,
            });
            channelId = userData.items?.[0]?.id;
          }
        }

        if (channelId) {
          // Get uploads playlist
          const channelData = await youtubeApi('channels', {
            part: 'contentDetails',
            id: channelId,
          });
          // A channel id that resolves to nothing used to throw here on
          // `items[0]` and surface as a blank failure.
          const uploadsPlaylistId =
            channelData.items?.[0]?.contentDetails?.relatedPlaylists?.uploads;
          if (!uploadsPlaylistId) {
            alert('That channel was found but has no public uploads playlist.');
            setIsLoadingBulk(false);
            return;
          }

          videoIds = await fetchAllPlaylistItems(uploadsPlaylistId);
        }
      }
    }

    if (videoIds.length === 0) {
      alert('No videos found. Make sure the URL is correct.');
      setIsLoadingBulk(false);
      return;
    }

    // videos.list takes a comma-separated id list, 50 at a time. One call per
    // batch rather than one per video, which is what this used to do through
    // Promise.all, where a single failure rejected everything.
    const items = [];
    for (let i = 0; i < videoIds.length; i += 50) {
      const data = await youtubeApi('videos', {
        part: 'snippet',
        id: videoIds.slice(i, i + 50).join(','),
      });
      items.push(...(data.items || []));
    }

    // Which of these are already in the database, so a refresh can show what
    // is actually new rather than the channel's whole back catalogue again.
    const haveIds = new Set(
      videos.map((v) => youtubeIdOf(v.url)).filter(Boolean)
    );

    const fetched = items.map((item) => {
      const snippet = item.snippet || {};
      const thumbs = snippet.thumbnails || {};
      return {
        id: item.id,
        title: snippet.title || '(untitled)',
        url: `https://www.youtube.com/watch?v=${item.id}`,
        publishedAt: snippet.publishedAt ? snippet.publishedAt.split('T')[0] : '',
        // Not every video carries every thumbnail size, and reaching straight
        // for .medium.url threw when it was absent. i.ytimg.com always serves
        // hqdefault for a public video, so it is a dependable last resort.
        thumbnail:
          thumbs.medium?.url ||
          thumbs.high?.url ||
          thumbs.default?.url ||
          `https://i.ytimg.com/vi/${item.id}/hqdefault.jpg`,
        channelTitle: snippet.channelTitle || '',
          channelId: snippet.channelId || '',
        description: snippet.description || '',
        alreadyHave: haveIds.has(item.id),
      };
    });

    if (fetched.length === 0) {
      alert('Found videos in that playlist, but YouTube returned no details for them.');
    } else if (fetched.length < videoIds.length) {
      // Private and deleted videos stay in a playlist but return no details.
      console.warn(
        `${videoIds.length - fetched.length} of ${videoIds.length} videos returned no details (private or deleted).`
      );
    }

    const isNew = fetched.filter((v) => !v.alreadyHave).length;
    setBulkScanSummary(
      `${videoIds.length} video(s) on the channel, ${isNew} not yet in the database.`
    );
    // New ones first: after a year away the point of a scan is what changed.
    fetched.sort((a, b) => Number(a.alreadyHave) - Number(b.alreadyHave));
    setFetchedVideos(fetched);
    // Pre-select only what is actually new, so importing does not re-add
    // everything that is already here.
    setSelectedVideos(new Set(fetched.filter((v) => !v.alreadyHave).map((v) => v.id)));
    setIsLoadingBulk(false);

  } catch (error) {
    console.error('Error fetching bulk videos:', error);
    alert('Failed to fetch videos. Please check the URL and try again.');
    setIsLoadingBulk(false);
  }
};

// Import selected videos in bulk
const importSelectedVideos = async () => {
  if (selectedVideos.size === 0) {
    alert('Please select at least one video to import');
    return;
  }

  const videosToImport = fetchedVideos.filter(v => selectedVideos.has(v.id));
  setBulkImportProgress({ current: 0, total: videosToImport.length });

  let successCount = 0;
  let failCount = 0;
  let duplicateCount = 0;

  // Names the detector proposes but which are NOT created during the run. See
  // the note further down for why.
  const proposedCreators = new Map();
  const knownCreatorKeys = new Set();
  for (const a of authors) {
    knownCreatorKeys.add(a.name.toLowerCase().replace(/[^a-z0-9]/g, ''));
    for (const l of a.socialLinks || []) {
      const m = String(l.url || '').match(/\/@?([A-Za-z0-9._-]+)\/?$/);
      if (m) knownCreatorKeys.add(m[1].toLowerCase().replace(/[^a-z0-9]/g, ''));
    }
  }

  for (let i = 0; i < videosToImport.length; i++) {
    const video = videosToImport[i];
    setBulkImportProgress({ current: i + 1, total: videosToImport.length });

    try {
      // Check if video already exists by URL
      const existingVideo = videos.find(v => v.url === video.url);
      if (existingVideo) {
        console.log(`Skipping duplicate video: ${video.title}`);
        duplicateCount++;
        continue;
      }

      // Fetch full video details including description for collaborator
      // detection. The description is not in the playlist listing, so this
      // second lookup is genuinely needed.
      let videoDescription = '';
      try {
        const detailData = await youtubeApi('videos', { part: 'snippet', id: video.id });
        // `detailData.items[0].snippet` threw outright when a video had gone
        // private between listing and import, which aborted the whole import.
        videoDescription = detailData.items?.[0]?.snippet?.description || '';
      } catch (err) {
        if (isYoutubeOutage(err)) {
          alert(`Stopped importing partway through.\n\n${err.message}`);
          break;
        }
        console.warn('Could not fetch details for', video.id, err);
      }

      // Try to find existing creator by channel name
      let creator = authors.find(a =>
        a.name.toLowerCase() === video.channelTitle.toLowerCase()
      );

      // If creator doesn't exist, create one
      if (!creator) {
        const photoUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${video.channelTitle}`;

        const creatorResponse = await fetch('/api/creators', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: video.channelTitle,
            channel: '',
            photo: photoUrl,
            socialLinks: []
          })
        });

        creator = await creatorResponse.json();
        setAuthors(prev => [...prev, creator]);
      }

      // Detect collaborators from title and description
      const { detectedCreators, creatorsToCreate } = await detectCollaboratorsFromTitle(
        video.title,
        videoDescription,
        video.channelTitle
      );

      /*
       * Collect proposed new collaborators; do NOT create them.
       *
       * This used to create every name the detector produced. Over the 582
       * videos waiting to be imported that would have been 151 new creators
       * against a database holding 144, including five raw YouTube channel
       * ids, "Netflix", "The Plumber", and "NO SUBTITLES", which had been
       * deleted from this database the same day.
       *
       * Existing creators are still linked automatically, which is the
       * valuable part and cannot invent anything. New ones are gathered and
       * shown once at the end, so a decision is made about a list of names
       * rather than silently, hundreds of times, mid-import.
       */
      for (const creatorData of creatorsToCreate) {
        if (!isPlausibleCreatorName(creatorData.name, knownCreatorKeys)) continue;
        const key = creatorData.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        const seen = proposedCreators.get(key);
        if (seen) {
          seen.count += 1;
        } else {
          proposedCreators.set(key, {
            name: creatorData.name,
            url: creatorData.url || '',
            count: 1,
            firstSeenOn: video.title,
          });
        }
      }

      // Only the channel's own creator and people already in the database.
      const allAuthorIds = [creator.id, ...detectedCreators].filter(
        (id, index, self) => self.indexOf(id) === index
      );

      // Add the video with all collaborators
      const videoResponse = await fetch('/api/videos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: video.title,
          url: video.url,
          date: video.publishedAt,
          // The importer knows the channel, so record it rather than leaving
          // it for a backfill script to find later.
          channelId: video.channelId || "",
          channelTitle: video.channelTitle || "",
          authorIds: allAuthorIds
        })
      });

      const newVideo = await videoResponse.json();
      setVideos(prev => [...prev, newVideo]);
      successCount++;

    } catch (error) {
      console.error('Error importing video:', video.title, error);
      failCount++;
    }
  }

  // Clear the bulk import UI
  setFetchedVideos([]);
  setSelectedVideos(new Set());
  setBulkImportUrl('');
  setBulkImportProgress({ current: 0, total: 0 });

  // Names worth considering, most frequently mentioned first. Somebody named
  // across twenty videos is far more likely to be a real collaborator than
  // somebody named once, so the ordering is the useful part.
  const proposals = [...proposedCreators.values()].sort((a, b) => b.count - a.count);
  setProposedCreators(proposals);

  let message = `Bulk import complete!\n✅ ${successCount} videos imported successfully`;
  if (duplicateCount > 0) {
    message += `\n⏭️ ${duplicateCount} duplicates skipped`;
  }
  if (failCount > 0) {
    message += `\n❌ ${failCount} videos failed`;
  }
  if (proposals.length > 0) {
    message +=
      `\n\n${proposals.length} possible new collaborator(s) were found and NOT created.` +
      `\nReview them below and add the ones that are really people.`;
  }

  alert(message);
};

// Handle adding an existing collaborator
const acceptExistingCollaborator = (collaborator) => {
  setNewVideo(prev => ({
    ...prev,
    authorIds: [...new Set([...prev.authorIds, collaborator.id])]
  }));
  setPendingCollaborators(prev => ({
    ...prev,
    existing: prev.existing.filter(c => c.id !== collaborator.id)
  }));
};

// Convert a handle to a full URL (defaults to Instagram if not a full URL)
const handleToUrl = (handleOrUrl) => {
  if (!handleOrUrl) return '';

  // If it's already a full URL, return as-is
  if (handleOrUrl.startsWith('http://') || handleOrUrl.startsWith('https://')) {
    return handleOrUrl;
  }

  // Otherwise, treat it as an Instagram handle and build the URL
  const cleanHandle = handleOrUrl.replace(/^@/, ''); // Remove @ if present
  return `https://www.instagram.com/${cleanHandle}`;
};

// Handle creating and adding a new collaborator
const acceptNewCollaborator = async (collaborator) => {
  try {
    const photoUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${collaborator.name}`;

    // Convert handle to full URL if needed
    const fullUrl = handleToUrl(collaborator.url);
    const platform = fullUrl ? detectPlatform(fullUrl) : '';

    const response = await fetch('/api/creators', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: collaborator.name,
        channel: fullUrl || '',
        photo: photoUrl,
        socialLinks: fullUrl ? [{
          platform: platform,
          url: fullUrl
        }] : []
      })
    });

    const newCreator = await response.json();
    setAuthors(prev => [...prev, newCreator]);

    setNewVideo(prev => ({
      ...prev,
      authorIds: [...new Set([...prev.authorIds, newCreator.id])]
    }));

    setPendingCollaborators(prev => ({
      ...prev,
      toCreate: prev.toCreate.filter(c => c.name !== collaborator.name)
    }));

    console.log('✅ Created and added new creator:', collaborator.name, 'with URL:', fullUrl);
  } catch (error) {
    console.error('Error creating creator:', error);
    alert('Error creating creator');
  }
};

// Handle rejecting a collaborator suggestion
const rejectCollaborator = (collaborator) => {
  if (collaborator.type === 'existing') {
    setPendingCollaborators(prev => ({
      ...prev,
      existing: prev.existing.filter(c => c.id !== collaborator.id)
    }));
  } else {
    setPendingCollaborators(prev => ({
      ...prev,
      toCreate: prev.toCreate.filter(c => c.name !== collaborator.name)
    }));
  }
};

// Clear all pending collaborators
const clearPendingCollaborators = () => {
  setPendingCollaborators({ existing: [], toCreate: [] });
};
  if (authPending()) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <p className="text-gray-500">Checking your session...</p>
      </div>
    );
  }

  if (needsAdminAccess()) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200 overflow-hidden">
            <div className="bg-orange-600 p-6 text-white text-center">
              <Shield className="w-16 h-16 mx-auto mb-3" />
              <h1 className="text-2xl font-bold">Admin Access Required</h1>
              <p className="mt-2 opacity-90">Enter password to access admin panel</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                  placeholder="Enter admin password"
                  autoFocus
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              {loginError && (
                <p
                  role="alert"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  {loginError}
                </p>
              )}
              <button
                onClick={handleAdminLogin}
                className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition flex items-center justify-center gap-2"
              >
                <Lock className="w-5 h-5" />
                Login
              </button>
              <button
                onClick={() => setActiveTab('search')}
                className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition"
              >
                Back to Public View
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Says out loud when YouTube lookups are degraded. The app used to
            fail silently here, which is what made it seem to work sporadically
            rather than to be out of quota. */}
        {youtubeNotice && (
          <div
            role="status"
            className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <span className="flex-1">{youtubeNotice}</span>
            <button
              onClick={() => setYoutubeNotice('')}
              className="shrink-0 text-amber-700 hover:text-amber-900"
              aria-label="Dismiss"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200 overflow-hidden">
          <div className="bg-orange-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-lg">
                  <Video className="w-10 h-10" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">
                    Friends of the Channel
                  </h1>
                  <p className="mt-2 opacity-90">
                    {isAdmin ? 'Admin Panel - Manage content and view analytics' : 'Track content creators and their collaborative projects'}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <button
                  onClick={handleAdminLogout}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              )}
            </div>
          </div>

          {activeTab !== 'author-page' && (
            <div className="border-b">
              <div className="flex flex-wrap">
                <button
                  onClick={() => setActiveTab('search')}
                  className={`flex-1 px-6 py-4 font-medium transition ${
                    activeTab === 'search'
                      ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Search className="w-5 h-5 inline mr-2" />
                  Search Creators
                </button>
                <button
                  onClick={() => {
                    setActiveTab('find-videos');
                    setSelectedAuthor(null);
                  }}
                  className={`flex-1 px-6 py-4 font-medium transition ${
                    activeTab === 'find-videos'
                      ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Video className="w-5 h-5 inline mr-2" />
                  Find Videos
                </button>
                <button
                  onClick={() => {
                    setActiveTab('leaderboard');
                    setSelectedAuthor(null);
                  }}
                  className={`flex-1 px-6 py-4 font-medium transition ${
                    activeTab === 'leaderboard'
                      ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Trophy className="w-5 h-5 inline mr-2" />
                  Leaderboard
                </button>
                {isAdmin && (
                  <>
                    <button
                      onClick={() => setActiveTab('videos')}
                      className={`flex-1 px-6 py-4 font-medium transition ${
                        activeTab === 'videos'
                          ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Video className="w-5 h-5 inline mr-2" />
                      Manage Videos
                    </button>
                    <button
                      onClick={() => setActiveTab('suggestions')}
                      className={`flex-1 px-6 py-4 font-medium transition relative ${
                        activeTab === 'suggestions'
                          ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Send className="w-5 h-5 inline mr-2" />
                      Suggestions
                      {suggestedVideos.length > 0 && (
                        <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {suggestedVideos.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('authors')}
                      className={`flex-1 px-6 py-4 font-medium transition ${
                        activeTab === 'authors'
                          ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Users className="w-5 h-5 inline mr-2" />
                      Manage Creators
                    </button>
                    <button
                      onClick={() => setActiveTab('stats')}
                      className={`flex-1 px-6 py-4 font-medium transition ${
                        activeTab === 'stats'
                          ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <TrendingUp className="w-5 h-5 inline mr-2" />
                      Analytics
                    </button>
                  </>
                )}
                {!isAdmin && (
                  <button
                    onClick={() => setActiveTab('admin-login')}
                    className="px-6 py-4 font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 transition flex items-center gap-2"
                  >
                    <Lock className="w-5 h-5" />
                    Admin
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="p-6">
            {activeTab === 'find-videos' && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Find videos</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Add creators to narrow the list. Adding more than one shows only the videos
                    they appear in <b>together</b>.
                  </p>
                </div>

                {/* Who is required */}
                <div className="rounded-xl ring-1 ring-slate-200 bg-white p-4 space-y-3">
                  {videoFilterCreators.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {videoFilterCreators.map((id) => {
                        const c = authors.find((a) => a.id === id);
                        if (!c) return null;
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-2 rounded-full bg-orange-100 text-orange-800 pl-1 pr-2 py-1 text-sm"
                          >
                            <Avatar src={c.photo} name={c.name} size={22} />
                            {c.name}
                            <button
                              onClick={() =>
                                setVideoFilterCreators((prev) => prev.filter((x) => x !== id))
                              }
                              className="text-orange-600 hover:text-orange-900"
                              aria-label={`Remove ${c.name}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </span>
                        );
                      })}
                      <button
                        onClick={() => setVideoFilterCreators([])}
                        className="text-sm text-slate-500 hover:text-slate-800 underline"
                      >
                        clear
                      </button>
                    </div>
                  )}

                  <input
                    type="text"
                    value={creatorPickerText}
                    onChange={(e) => setCreatorPickerText(e.target.value)}
                    placeholder={
                      videoFilterCreators.length ? 'Add another creator...' : 'Add a creator...'
                    }
                    className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />

                  {creatorSuggestions.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {creatorSuggestions.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => {
                            setVideoFilterCreators((prev) => [...prev, c.id]);
                            setCreatorPickerText('');
                          }}
                          className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-200 hover:ring-orange-400 hover:bg-orange-50 pl-1 pr-3 py-1 text-sm transition"
                        >
                          <Avatar src={c.photo} name={c.name} size={22} />
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {creatorPickerText && creatorSuggestions.length === 0 && (
                    <p className="text-sm text-slate-500">
                      Nobody matching that also appears in the current results.
                    </p>
                  )}

                  <input
                    type="text"
                    value={videoFilterText}
                    onChange={(e) => setVideoFilterText(e.target.value)}
                    placeholder="Search titles and channels..."
                    className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <p className="text-sm text-slate-600">
                  {searchedVideos.length} video{searchedVideos.length === 1 ? '' : 's'}
                  {videoFilterCreators.length > 1 && ' featuring all of them'}
                </p>

                <div className="space-y-3">
                  {searchedVideos.slice(0, 100).map((video) => (
                    <div
                      key={video.id}
                      className="bg-white ring-1 ring-slate-200 rounded-xl p-3 hover:shadow-md transition-shadow flex items-start gap-4"
                    >
                      <a
                        href={video.url || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <VideoThumb url={video.url} title={video.title} size="md" />
                      </a>
                      <div className="flex-1 min-w-0">
                        <a
                          href={video.url || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-slate-900 hover:text-orange-600"
                        >
                          {video.title}
                        </a>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 mt-1">
                          {video.channelTitle && (
                            <span className="inline-flex items-center gap-1">
                              <Video className="w-3.5 h-3.5" />
                              {video.channelTitle}
                            </span>
                          )}
                          {video.date && <span>{video.date}</span>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {video.authorIds.map((id) => {
                            const c = authors.find((a) => a.id === id);
                            if (!c) return null;
                            const chosen = videoFilterCreators.includes(id);
                            return (
                              <button
                                key={id}
                                onClick={() =>
                                  setVideoFilterCreators((prev) =>
                                    chosen ? prev.filter((x) => x !== id) : [...prev, id]
                                  )
                                }
                                title={chosen ? 'Remove from filter' : 'Add to filter'}
                                className={`px-2 py-0.5 rounded-full text-xs transition ${
                                  chosen
                                    ? 'bg-orange-600 text-white'
                                    : 'bg-slate-100 text-slate-700 hover:bg-orange-100 hover:text-orange-800'
                                }`}
                              >
                                {c.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                  {searchedVideos.length === 0 && (
                    <p className="text-slate-500 py-8 text-center">
                      No videos match. Try removing a creator from the filter.
                    </p>
                  )}
                  {searchedVideos.length > 100 && (
                    <p className="text-sm text-slate-500 text-center py-2">
                      Showing the first 100 of {searchedVideos.length}. Add a creator to narrow it.
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'search' && (
              <div className="space-y-6">
                <div className="bg-white ring-1 ring-slate-200 rounded-lg p-6">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Search for Creators</h2>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by name or channel..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-lg"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowSuggestionForm(!showSuggestionForm)}
                    className="w-full p-4 flex items-center justify-between hover:bg-blue-100 transition"
                  >
                    <div className="flex items-center gap-2">
                      <Send className="w-5 h-5 text-blue-700" />
                      <h3 className="text-lg font-semibold text-blue-900">
                        Suggest a Video
                      </h3>
                    </div>
                    {showSuggestionForm ? (
                      <ChevronUp className="w-5 h-5 text-blue-700" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-blue-700" />
                    )}
                  </button>
                  
                  {showSuggestionForm && (
                    <div className="p-6 pt-0 border-t border-blue-200">
                      <p className="text-sm text-gray-600 mb-4">
                        Know a great collaboration video? Just paste the YouTube URL and we'll handle the rest!
                      </p>
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder="YouTube Video URL (e.g., https://www.youtube.com/watch?v=...)"
                          value={newSuggestion.url}
                          onChange={(e) => setNewSuggestion({ url: e.target.value })}
                          className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={submitSuggestion}
                          disabled={!newSuggestion.url.trim()}
                          className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          <Send className="w-5 h-5" />
                          Submit Suggestion
                        </button>
                      </div>
                    </div>
                  )}

                  {suggestionSubmitted && (
                    <div className="mt-4 bg-green-50 border-2 border-green-400 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-green-900">Thank you!</p>
                        <p className="text-sm text-green-700">Your video suggestion has been submitted for review.</p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">
                      {searchQuery ? `Search Results (${getFilteredAuthors().length})` : `All Creators (${authors.length})`}
                    </h2>
                    {isAdmin && (
                      <button
                        onClick={() => {
                          if (selectedCreators.size > 0) {
                            setSelectedCreators(new Set());
                          }
                        }}
                        className={`text-sm px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                          selectedCreators.size > 0
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        {selectedCreators.size > 0 ? `${selectedCreators.size} Selected` : 'Merge Mode'}
                      </button>
                    )}
                  </div>

                  {isAdmin && selectedCreators.size > 0 && (
                    <div className="mb-4 bg-purple-50 border-2 border-purple-300 rounded-lg p-4 flex items-center justify-between">
                      <span className="text-sm font-medium text-purple-900">
                        {selectedCreators.size} creator(s) selected for merging
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedCreators(new Set())}
                          className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1"
                        >
                          Clear
                        </button>
                        <button
                          onClick={() => {
                            const selectedAuthors = authors.filter(a => selectedCreators.has(a.id));
                            mergeCreators(selectedAuthors);
                          }}
                          disabled={selectedCreators.size < 2}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm flex items-center gap-1 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          <Users className="w-4 h-4" />
                          Merge Selected
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getFilteredAuthors().map(author => {
                      const authorVideos = getAuthorVideos(author.id);
                      const collabCount = authorVideos.filter(v => v.authorIds.length > 1).length;
                      return (
                        <div key={author.id}>
                          {editingAuthor?.id === author.id ? (
                            <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4">
                              <h3 className="font-semibold text-sm mb-3 text-blue-900">Editing Creator</h3>
                              <div className="space-y-3">
                                <input
                                  type="text"
                                  placeholder="Creator name"
                                  value={editingAuthor.name}
                                  onChange={(e) => setEditingAuthor({ ...editingAuthor, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                                <input
                                  type="text"
                                  placeholder="Photo URL"
                                  value={editingAuthor.photo}
                                  onChange={(e) => setEditingAuthor({ ...editingAuthor, photo: e.target.value })}
                                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />

                                <div className="border-t border-blue-200 pt-3">
                                  <label className="block text-xs font-medium text-gray-700 mb-2">
                                    Social Media Links
                                  </label>
                                  {(editingAuthor.socialLinks || []).map((link, index) => (
                                    <div key={index} className="flex gap-2 mb-2">
                                      <input
                                        type="text"
                                        placeholder="Platform"
                                        value={link.platform}
                                        readOnly
                                        className="w-24 px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs"
                                      />
                                      <input
                                        type="text"
                                        placeholder="https://..."
                                        value={link.url}
                                        onChange={(e) => updateEditingSocialLink(index, 'url', e.target.value)}
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                                      />
                                      {editingAuthor.socialLinks.length > 1 && (
                                        <button
                                          onClick={() => removeSocialLinkFromEditingAuthor(index)}
                                          className="px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    onClick={addSocialLinkToEditingAuthor}
                                    className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Add Platform
                                  </button>
                                </div>

                                <div className="flex gap-2 pt-2">
                                  <button
                                    onClick={updateAuthor}
                                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditingAuthor}
                                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`bg-white rounded-xl p-4 transition-shadow ${
                                selectedCreators.has(author.id)
                                  ? 'ring-2 ring-orange-400 shadow-sm'
                                  : 'ring-1 ring-slate-200 hover:shadow-md'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3 flex-1">
                                  {isAdmin && (
                                    <input
                                      type="checkbox"
                                      checked={selectedCreators.has(author.id)}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const newSelected = new Set(selectedCreators);
                                        if (e.target.checked) {
                                          newSelected.add(author.id);
                                        } else {
                                          newSelected.delete(author.id);
                                        }
                                        setSelectedCreators(newSelected);
                                      }}
                                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                                    />
                                  )}
                                  <Avatar
                                    src={author.photo}
                                    name={author.name}
                                    size={48}
                                    onClick={() => viewAuthorPage(author)}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <h3
                                      className="font-semibold text-base text-slate-900 cursor-pointer hover:text-orange-600 transition-colors truncate"
                                      onClick={() => viewAuthorPage(author)}
                                    >
                                      {author.name}
                                    </h3>
                                    <div className="flex gap-3 text-sm text-slate-500 mt-0.5">
                                      <span className="flex items-center gap-1">
                                        <Video className="w-3.5 h-3.5" />
                                        {authorVideos.length} video{authorVideos.length === 1 ? '' : 's'}
                                      </span>
                                      {collabCount > 0 && (
                                        <span className="flex items-center gap-1">
                                          <Users className="w-3.5 h-3.5" />
                                          {collabCount} collab{collabCount === 1 ? '' : 's'}
                                        </span>
                                      )}
                                    </div>
                                    {/* These were absent from this card entirely, which is
                                        most of why it looked empty: the links existed in the
                                        database and only the admin list ever showed them. The
                                        channel URL was printed as bare text rather than being
                                        something you could click. */}
                                    <SocialLinks
                                      links={author.socialLinks}
                                      channel={author.channel}
                                      className="mt-2"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {isAdmin && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          startEditingAuthor(author);
                                        }}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                        title="Edit creator"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteAuthor(author.id);
                                        }}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                        title="Delete creator"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                  <div className="text-orange-600 cursor-pointer" onClick={() => viewAuthorPage(author)}>
                                    <ArrowLeft className="w-5 h-5 transform rotate-180" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {getFilteredAuthors().length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Search className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No creators found{searchQuery && ` matching "${searchQuery}"`}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="space-y-6">
                <div className="bg-white ring-1 ring-slate-200 rounded-lg p-6">
                  <h2 className="text-2xl font-bold mb-2 text-gray-800 flex items-center gap-2">
                    <Trophy className="w-8 h-8 text-orange-600" />
                    Collaboration Leaderboard
                  </h2>
                  <p className="text-gray-600">Top creators in the community</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <div className="bg-orange-600 text-white rounded-t-lg p-4">
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <Award className="w-6 h-6" />
                        Most Collaborations
                      </h3>
                      <p className="text-sm opacity-90">Creators with the most collaborative videos</p>
                    </div>
                    <div className="bg-white rounded-b-lg border-2 border-orange-200 overflow-hidden">
                      {(() => {
                        const data = getLeaderboardData();
                        return data.mostCollabs.map((stat, index) => (
                          <div
                            key={stat.author.id}
                            onClick={() => viewAuthorPage(stat.author)}
                            className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-orange-50 transition border-b border-gray-100 ${
                              index === 0 ? 'bg-yellow-50' : ''
                            }`}
                          >
                            <div className={`text-2xl font-bold w-8 ${
                              index === 0 ? 'text-yellow-500' : 
                              index === 1 ? 'text-gray-400' : 
                              index === 2 ? 'text-orange-400' : 'text-gray-300'
                            }`}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </div>
                            <img
                              src={stat.author.photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${stat.author.name}`}
                              alt={stat.author.name}
                              className="w-12 h-12 rounded-full border-2 border-orange-300"
                            />
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">{stat.author.name}</h4>
                              {stat.author.channel && (
                                <p className="text-sm text-gray-600">{stat.author.channel}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-orange-600">{stat.collabCount}</div>
                              <div className="text-xs text-gray-600">collabs</div>
                            </div>
                          </div>
                        ));
                      })()}
                      {getLeaderboardData().mostCollabs.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Award className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No collaborations yet</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="bg-emerald-600 text-white rounded-t-lg p-4">
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <Users className="w-6 h-6" />
                        Most Collaborators
                      </h3>
                      <p className="text-sm opacity-90">Creators who work with the most people</p>
                    </div>
                    <div className="bg-white rounded-b-lg border-2 border-green-200 overflow-hidden">
                      {(() => {
                        const data = getLeaderboardData();
                        return data.mostCollaborators.map((stat, index) => (
                          <div
                            key={stat.author.id}
                            onClick={() => viewAuthorPage(stat.author)}
                            className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-green-50 transition border-b border-gray-100 ${
                              index === 0 ? 'bg-green-50' : ''
                            }`}
                          >
                            <div className={`text-2xl font-bold w-8 ${
                              index === 0 ? 'text-yellow-500' : 
                              index === 1 ? 'text-gray-400' : 
                              index === 2 ? 'text-orange-400' : 'text-gray-300'
                            }`}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </div>
                            <img
                              src={stat.author.photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${stat.author.name}`}
                              alt={stat.author.name}
                              className="w-12 h-12 rounded-full border-2 border-green-300"
                            />
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">{stat.author.name}</h4>
                              {stat.author.channel && (
                                <p className="text-sm text-gray-600">{stat.author.channel}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-green-600">{stat.collaboratorCount}</div>
                              <div className="text-xs text-gray-600">people</div>
                            </div>
                          </div>
                        ));
                      })()}
                      {getLeaderboardData().mostCollaborators.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No collaborations yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'author-page' && selectedAuthor && (
              <div className="space-y-6">
                <button
                  onClick={() => {
                    setActiveTab('search');
                    setSelectedAuthor(null);
                  }}
                  className="flex items-center gap-2 text-orange-600 hover:text-orange-800 font-medium mb-4"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Search
                </button>

                <div className="bg-white ring-1 ring-slate-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <img 
                        src={selectedAuthor.photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedAuthor.name}`} 
                        alt={selectedAuthor.name}
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedAuthor.name}</h2>
                        {selectedAuthor.channel && (
                          <a
                            href={selectedAuthor.channel}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-lg text-blue-600 hover:text-blue-800 flex items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {selectedAuthor.channel}
                          </a>
                        )}
                        {selectedAuthor.socialLinks && selectedAuthor.socialLinks.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {selectedAuthor.socialLinks.map((link, idx) => (
                              <a
                                key={idx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm hover:bg-gray-100 transition flex items-center gap-1 border border-gray-300"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {link.platform}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <User className="w-16 h-16 text-orange-500 opacity-30" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {getAuthorVideos(selectedAuthor.id).length}
                      </div>
                      <div className="text-sm text-gray-600">Total Videos</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-600">
                        {getAuthorVideos(selectedAuthor.id).filter(v => v.authorIds.length === 1).length}
                      </div>
                      <div className="text-sm text-gray-600">Solo Videos</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {getAuthorVideos(selectedAuthor.id).filter(v => v.authorIds.length > 1).length}
                      </div>
                      <div className="text-sm text-gray-600">Collaborations</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {getCollaborators(selectedAuthor.id).length}
                      </div>
                      <div className="text-sm text-gray-600">Collaborators</div>
                    </div>
                  </div>
                </div>

                {getCollaboratorCounts(selectedAuthor.id).length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Frequent collaborators</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Sorted by how often they appear together. Click a row to see just those
                      videos; click the name to open their profile.
                    </p>
                    <div className="overflow-hidden rounded-xl ring-1 ring-slate-200 bg-white">
                      <table className="w-full text-sm">
                        <tbody>
                          {getCollaboratorCounts(selectedAuthor.id).map(({ creator, count }) => {
                            const active = pairWith === creator.id;
                            return (
                              <tr
                                key={creator.id}
                                onClick={() => setPairWith(active ? null : creator.id)}
                                className={`border-t border-slate-100 first:border-t-0 cursor-pointer transition-colors ${
                                  active ? 'bg-orange-50' : 'hover:bg-slate-50'
                                }`}
                              >
                                <td className="px-3 py-2 w-10">
                                  <Avatar src={creator.photo} name={creator.name} size={32} />
                                </td>
                                <td className="px-1 py-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      viewAuthorPage(creator);
                                    }}
                                    className="font-medium text-slate-900 hover:text-orange-600 hover:underline text-left"
                                  >
                                    {creator.name}
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap text-slate-600">
                                  {count} together
                                </td>
                                <td className="px-3 py-2 text-right w-24">
                                  <span
                                    className={`text-xs ${active ? 'text-orange-700 font-medium' : 'text-slate-400'}`}
                                  >
                                    {active ? 'showing' : 'show'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    {pairWith
                      ? `With ${getAuthorName(pairWith)} (${getSharedVideos(selectedAuthor.id, pairWith).length})`
                      : `All Videos (${getAuthorVideos(selectedAuthor.id).length})`}
                    {pairWith && (
                      <button
                        onClick={() => setPairWith(null)}
                        className="ml-3 text-sm font-normal text-orange-700 hover:underline"
                      >
                        show all {getAuthorVideos(selectedAuthor.id).length}
                      </button>
                    )}
                  </h3>
                  <div className="space-y-4">
                    {(pairWith
                      ? getSharedVideos(selectedAuthor.id, pairWith)
                      : getAuthorVideos(selectedAuthor.id)
                    )
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map(video => (
                        <div key={video.id} className="bg-white ring-1 ring-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start gap-4">
                            {/* Derived from the video id, so every video has one
                                without a stored field or an API call. */}
                            <a
                              href={video.url || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <VideoThumb url={video.url} title={video.title} size="md" />
                            </a>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-base mb-2 text-slate-900">{video.title}</h4>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {video.authorIds.map(authorId => (
                                  <button
                                    key={authorId}
                                    onClick={() => {
                                      const author = authors.find(a => a.id === authorId);
                                      if (author && author.id !== selectedAuthor.id) {
                                        viewAuthorPage(author);
                                      }
                                    }}
                                    className={`px-3 py-1 rounded-full text-sm ${
                                      authorId === selectedAuthor.id
                                        ? 'bg-blue-100 text-blue-700 cursor-default'
                                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200 cursor-pointer'
                                    }`}
                                  >
                                    {getAuthorName(authorId)}
                                  </button>
                                ))}
                                {video.authorIds.length > 1 && (
                                  <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700 font-medium">
                                    Collaboration
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                                {/* Whose channel it went out on, which the page
                                    could not say before: a creator's videos and
                                    the videos they merely appear in looked
                                    identical. */}
                                {video.channelTitle && (
                                  <span className="inline-flex items-center gap-1">
                                    <Video className="w-3.5 h-3.5" />
                                    {video.channelTitle}
                                    {video.channelTitle === selectedAuthor.name && (
                                      <span className="text-slate-400">(their channel)</span>
                                    )}
                                  </span>
                                )}
                                {video.date && <span>{video.date}</span>}
                                {video.url && (
                                  <a
                                    href={video.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Watch <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    {getAuthorVideos(selectedAuthor.id).length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No videos found for this creator</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'suggestions' && isAdmin && (
              <div className="space-y-6">
                <div className="bg-white ring-1 ring-slate-200 rounded-lg p-6">
                  <h2 className="text-xl font-semibold mb-2 text-gray-800 flex items-center gap-2">
                    <Send className="w-6 h-6" />
                    Video Suggestions
                  </h2>
                  <p className="text-sm text-gray-600">
                    Review and approve or deny video suggestions from users
                  </p>
                </div>

                {suggestedVideos.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Send className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No pending suggestions</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {suggestedVideos.map(suggestion => (
                      <div key={suggestion.id} className="bg-white border-2 border-orange-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="space-y-2 text-sm text-gray-600">
                              <p>
                                <strong className="text-gray-900">Video URL:</strong>{' '}
                                <a
                                  href={suggestion.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 break-all"
                                >
                                  {suggestion.url}
                                </a>
                              </p>
                              <p className="text-xs text-gray-500">
                                Submitted: {new Date(suggestion.submittedAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => approveSuggestion(suggestion)}
                            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                            Review & Add
                          </button>
                          <button
                            onClick={() => denySuggestion(suggestion.id)}
                            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2"
                          >
                            <XCircle className="w-5 h-5" />
                            Deny
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'authors' && isAdmin && (
              <div className="space-y-6">
                {/* Suggested fixes, from src/lib/profileChecks.js. The same
                    checks scripts/check-profiles.mjs runs, surfaced where the
                    fixing actually happens. */}
                {profileIssues.length > 0 && (
                  <div className="rounded-xl ring-1 ring-amber-300 bg-amber-50 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h2 className="font-semibold text-amber-900">
                          {profileIssues.length} record{profileIssues.length === 1 ? '' : 's'} worth
                          a look
                        </h2>
                        <p className="text-sm text-amber-900/80 mt-0.5">
                          The collaborator detection reads names out of video titles, so it
                          sometimes records one person twice or invents one entirely. Each
                          suggestion below is a guess; nothing is applied without you confirming.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      {profileIssues.map((issue, i) => (
                        <div
                          key={`${issue.kind}-${issue.creator.id}-${i}`}
                          className="bg-white rounded-lg ring-1 ring-amber-200 p-3 flex items-start gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs uppercase tracking-wide text-amber-800 font-medium">
                                {ISSUE_LABELS[issue.kind] || issue.kind}
                              </span>
                              {issue.confidence === 'medium' && (
                                <span className="text-xs text-slate-500">
                                  less certain, check before acting
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-800 mt-1">{issue.message}</p>
                            {issue.suggestion.type === 'merge' && (
                              <p className="text-xs text-slate-500 mt-1">
                                Suggested: keep #{issue.suggestion.keep.id}{' '}
                                {issue.suggestion.keep.name} ({issue.suggestion.keep.videoCount}{' '}
                                videos), merge in #{issue.suggestion.drop.id}{' '}
                                {issue.suggestion.drop.name} ({issue.suggestion.drop.videoCount})
                                {issue.suggestion.rename
                                  ? `, renaming to "${issue.suggestion.rename}"`
                                  : ''}
                              </p>
                            )}
                          </div>

                          <div className="flex gap-2 shrink-0">
                            {issue.suggestion.type === 'merge' && (
                              <button
                                onClick={() => applyMergeSuggestion(issue.suggestion)}
                                className="text-sm bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 transition"
                              >
                                Merge
                              </button>
                            )}
                            {issue.suggestion.type === 'rename' && (
                              <button
                                onClick={() => applyRenameSuggestion(issue.creator)}
                                className="text-sm bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 transition"
                              >
                                Rename
                              </button>
                            )}
                            {issue.suggestion.type === 'delete' && (
                              <button
                                onClick={() => applyDeleteSuggestion(issue.creator)}
                                className="text-sm bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition"
                              >
                                Delete
                              </button>
                            )}
                            <button
                              onClick={() => viewAuthorPage(issue.creator)}
                              className="text-sm ring-1 ring-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-orange-50 rounded-lg p-6">
                  <h2 className="text-xl font-semibold mb-4 text-orange-900">Add New Creator</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Creator name *"
                        value={newAuthor.name}
                        onChange={(e) => setNewAuthor({ ...newAuthor, name: e.target.value })}
                        className="px-4 py-2 border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <input
                        type="text"
                        placeholder="Photo URL (optional)"
                        value={newAuthor.photo}
                        onChange={(e) => setNewAuthor({ ...newAuthor, photo: e.target.value })}
                        className="px-4 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div className="border-t border-orange-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Social Media Links
                        </label>
                        <button
                          onClick={fetchCreatorInfoFromYouTube}
                          className="text-xs bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 transition flex items-center gap-1"
                          title="Auto-fill name and photo from YouTube or Instagram URL"
                        >
                          📺 Fetch Info
                        </button>
                      </div>
                      {newAuthor.socialLinks.map((link, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="Platform (auto-detected)"
                            value={link.platform}
                            readOnly
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                          />
                          <input
                            type="text"
                            placeholder="https://youtube.com/@channel or other URL..."
                            value={link.url}
                            onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          {newAuthor.socialLinks.length > 1 && (
                            <button
                              onClick={() => removeSocialLinkFromAuthor(index)}
                              className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={addSocialLinkToAuthor}
                        className="mt-2 text-sm text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Another Platform
                      </button>
                    </div>

                    <button
                      onClick={addAuthor}
                      disabled={!newAuthor.name.trim()}
                      className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-5 h-5" />
                      Add Creator
                    </button>
                  </div>
                </div>

                {/* Bulk Actions & Duplicate Finder */}
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-red-900">Bulk Actions & Cleanup</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={refreshAllCreatorPhotos}
                        className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-1"
                        title="Refresh all profile pictures from social media"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refresh Photos
                      </button>
                      <button
                        onClick={() => setShowDuplicates(!showDuplicates)}
                        className="text-sm bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition"
                      >
                        {showDuplicates ? 'Hide' : 'Find'} Duplicates
                      </button>
                    </div>
                  </div>

                  {showDuplicates && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <h4 className="font-medium text-yellow-900 mb-2">Potential Duplicates Found:</h4>
                      {findDuplicateCreators().length === 0 ? (
                        <p className="text-sm text-gray-600">No duplicates found!</p>
                      ) : (
                        <div className="space-y-2">
                          {findDuplicateCreators().map((group, idx) => (
                            <div key={idx} className="bg-white p-3 rounded border border-yellow-200">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900 mb-1">Duplicate group:</p>
                                  {group.map(author => (
                                    <div key={author.id} className="text-sm text-gray-700 ml-4">
                                      • ID {author.id}: {author.name} ({videos.filter(v => v.authorIds.includes(author.id)).length} videos)
                                    </div>
                                  ))}
                                </div>
                                <button
                                  onClick={() => mergeCreators(group)}
                                  className="ml-3 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition text-sm flex items-center gap-1"
                                  title="Merge these creators into one"
                                >
                                  <Users className="w-4 h-4" />
                                  Merge
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedCreators.size > 0 && (
                    <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-red-300">
                      <span className="text-sm font-medium text-gray-900">
                        {selectedCreators.size} creator(s) selected
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedCreators(new Set())}
                          className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1"
                        >
                          Clear
                        </button>
                        <button
                          onClick={bulkDeleteCreators}
                          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition text-sm flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Selected
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="text-xl font-semibold mb-4">All Creators ({authors.length})</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {authors.map(author => (
                      <div key={author.id}>
                        {editingAuthor?.id === author.id ? (
                          <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4">
                            <h3 className="font-semibold text-sm mb-3 text-blue-900">Editing Creator</h3>
                            <div className="space-y-3">
                              <input
                                type="text"
                                placeholder="Creator name"
                                value={editingAuthor.name}
                                onChange={(e) => setEditingAuthor({ ...editingAuthor, name: e.target.value })}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                              <input
                                type="text"
                                placeholder="Photo URL"
                                value={editingAuthor.photo}
                                onChange={(e) => setEditingAuthor({ ...editingAuthor, photo: e.target.value })}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />

                              <div className="border-t border-blue-200 pt-3">
                                <label className="block text-xs font-medium text-gray-700 mb-2">
                                  Social Media Links
                                </label>
                                {(editingAuthor.socialLinks || []).map((link, index) => (
                                  <div key={index} className="flex gap-2 mb-2">
                                    <input
                                      type="text"
                                      placeholder="Platform"
                                      value={link.platform}
                                      readOnly
                                      className="w-24 px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs"
                                    />
                                    <input
                                      type="text"
                                      placeholder="https://..."
                                      value={link.url}
                                      onChange={(e) => updateEditingSocialLink(index, 'url', e.target.value)}
                                      className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                                    />
                                    {editingAuthor.socialLinks.length > 1 && (
                                      <button
                                        onClick={() => removeSocialLinkFromEditingAuthor(index)}
                                        className="px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button
                                  onClick={addSocialLinkToEditingAuthor}
                                  className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Platform
                                </button>
                              </div>

                              <div className="flex gap-2 pt-2">
                                <button
                                  onClick={updateAuthor}
                                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEditingAuthor}
                                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-md transition">
                            <div className="flex items-center gap-3 flex-1">
                              <input
                                type="checkbox"
                                checked={selectedCreators.has(author.id)}
                                onChange={(e) => {
                                  const newSelected = new Set(selectedCreators);
                                  if (e.target.checked) {
                                    newSelected.add(author.id);
                                  } else {
                                    newSelected.delete(author.id);
                                  }
                                  setSelectedCreators(newSelected);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                              />
                              <img
                                src={author.photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${author.name}`}
                                alt={author.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-purple-200"
                              />
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg">{author.name}</h3>
                                {getSocialLinks(author).length > 0 && (
                                  <div className="space-y-1 mt-1">
                                    {getSocialLinks(author).map((link, i) => (
                                      <a
                                        key={i}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                      >
                                        {getPlatformIcon(link.url)} {link.platform}
                                      </a>
                                    ))}
                                  </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                  {videos.filter(v => v.authorIds.includes(author.id)).length} videos
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => viewAuthorPage(author)}
                                className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded"
                              >
                                <User className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => startEditingAuthor(author)}
                                className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => deleteAuthor(author.id)}
                                className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'videos' && isAdmin && (
              <div className="space-y-6">
                {/* Single-creator videos that name somebody else. */}
                <div className="bg-white ring-1 ring-slate-200 rounded-lg p-6 border-2 border-emerald-200">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-xl font-semibold text-emerald-900 flex items-center gap-2">
                        <Users className="w-6 h-6" />
                        Find missed collaborators
                      </h2>
                      <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                        Videos with one creator whose title or description names somebody else.
                        Automatic detection can only be sure of a few, because a description is a
                        credits list mixed with advertising, so these are for you to judge.
                      </p>
                    </div>
                    <button
                      onClick={loadSoloVideosForReview}
                      disabled={reviewLoading}
                      className="bg-emerald-700 text-white px-4 py-2 rounded-lg hover:bg-emerald-800 transition disabled:bg-gray-400 whitespace-nowrap"
                    >
                      {reviewLoading ? 'Loading...' : 'Review single-creator videos'}
                    </button>
                  </div>

                  {soloReview.length > 0 && (
                    <div className="mt-4 space-y-3 max-h-[32rem] overflow-y-auto">
                      <p className="text-sm text-slate-600">
                        {soloReview.length} video{soloReview.length === 1 ? '' : 's'} to look at.
                        Tick who was actually in it.
                      </p>
                      {soloReview.map(({ video, suggestions }) => (
                        <div
                          key={video.id}
                          className="rounded-lg ring-1 ring-slate-200 p-3 flex items-start gap-3"
                        >
                          <a href={video.url} target="_blank" rel="noopener noreferrer">
                            <VideoThumb url={video.url} title={video.title} size="sm" />
                          </a>
                          <div className="flex-1 min-w-0">
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-slate-900 hover:text-emerald-700"
                            >
                              {video.title}
                            </a>
                            <p className="text-xs text-slate-500">
                              {video.channelTitle} · currently{' '}
                              {(video.authorIds || []).map(getAuthorName).join(', ') || 'nobody'}
                            </p>

                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {suggestions.map((s) => {
                                const picked = (reviewPicks[video.id] || []).includes(s.creatorId);
                                return (
                                  <button
                                    key={s.creatorId}
                                    title={s.why}
                                    onClick={() =>
                                      setReviewPicks((prev) => {
                                        const cur = prev[video.id] || [];
                                        return {
                                          ...prev,
                                          [video.id]: picked
                                            ? cur.filter((x) => x !== s.creatorId)
                                            : [...cur, s.creatorId],
                                        };
                                      })
                                    }
                                    className={`px-2 py-0.5 rounded-full text-xs transition ${
                                      picked
                                        ? 'bg-emerald-600 text-white'
                                        : s.strength === 'strong'
                                        ? 'bg-emerald-100 text-emerald-900 hover:bg-emerald-200'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                  >
                                    {s.name}
                                    {s.strength === 'strong' && ' ✓'}
                                  </button>
                                );
                              })}
                            </div>
                            {/* The evidence, so a decision does not need the
                                video opening in another tab. */}
                            <p className="text-xs text-slate-400 mt-1 truncate">
                              {suggestions[0]?.why}
                            </p>
                          </div>

                          <div className="flex flex-col gap-1 shrink-0">
                            <button
                              disabled={!(reviewPicks[video.id] || []).length}
                              onClick={() => attachCollaborators(video, reviewPicks[video.id] || [])}
                              className="text-sm bg-emerald-600 text-white px-3 py-1 rounded-lg hover:bg-emerald-700 transition disabled:bg-slate-300"
                            >
                              Add
                            </button>
                            <button
                              onClick={() =>
                                setSoloReview((prev) => prev.filter((r) => r.video.id !== video.id))
                              }
                              className="text-xs text-slate-500 hover:text-slate-800"
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bulk Import Section */}
                <div className="bg-white ring-1 ring-slate-200 rounded-lg p-6 border-2 border-purple-200">
                  <h2 className="text-xl font-semibold mb-4 text-purple-900 flex items-center gap-2">
                    <Video className="w-6 h-6" />
                    Bulk Import Videos
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Import multiple videos at once from a YouTube channel or playlist
                  </p>

                  {/* Names a bulk import found but deliberately did not create.
                      Deciding about a list once beats deciding silently,
                      hundreds of times, mid-import. */}
                  {proposedCreators.length > 0 && (
                    <div className="mb-4 rounded-lg bg-amber-50 ring-1 ring-amber-300 p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <h3 className="font-semibold text-amber-900">
                            {proposedCreators.length} possible new collaborator
                            {proposedCreators.length === 1 ? '' : 's'}
                          </h3>
                          <p className="text-sm text-amber-900/80 mt-0.5">
                            Found while importing and <b>not</b> created. Mentioned most often
                            first, which is the best guide to who is really a collaborator rather
                            than a passing reference or a sponsor.
                          </p>
                        </div>
                        <button
                          onClick={() => setProposedCreators([])}
                          className="text-sm text-amber-800 hover:text-amber-950 underline"
                        >
                          Dismiss all
                        </button>
                      </div>

                      <div className="mt-3 space-y-1.5 max-h-80 overflow-auto">
                        {proposedCreators.map((p) => (
                          <div
                            key={p.name}
                            className="bg-white rounded-lg ring-1 ring-amber-200 px-3 py-2 flex items-center gap-3"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-slate-900">{p.name}</span>
                              <span className="text-slate-500 text-sm ml-2">
                                mentioned {p.count} time{p.count === 1 ? '' : 's'}
                              </span>
                              <p className="text-xs text-slate-500 truncate">
                                first on "{p.firstSeenOn}"
                              </p>
                            </div>
                            <button
                              onClick={() => createProposedCreator(p)}
                              className="text-sm bg-amber-600 text-white px-3 py-1 rounded-lg hover:bg-amber-700 transition shrink-0"
                            >
                              Create
                            </button>
                            <button
                              onClick={() =>
                                setProposedCreators((prev) => prev.filter((x) => x.name !== p.name))
                              }
                              className="text-sm text-slate-500 hover:text-slate-800 shrink-0"
                            >
                              Ignore
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* The common case: what has appeared since we last looked.
                      Uses the channels already recorded against your videos, so
                      there is nothing to paste, and stops walking each channel
                      as soon as it reaches videos you already have. */}
                  <div className="mb-4 rounded-lg bg-purple-50 ring-1 ring-purple-200 p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm">
                      <p className="font-medium text-purple-900">Check for new uploads</p>
                      <p className="text-purple-900/70">
                        Looks at every channel already in the database and finds anything posted
                        since the newest video you have.
                      </p>
                    </div>
                    <button
                      onClick={checkTrackedChannelsForNew}
                      disabled={isLoadingBulk}
                      className="bg-purple-700 text-white px-4 py-2 rounded-lg hover:bg-purple-800 transition disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoadingBulk ? 'animate-spin' : ''}`} />
                      {isLoadingBulk ? 'Checking...' : 'Check all channels'}
                    </button>
                  </div>

                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      placeholder="YouTube channel or playlist URL (e.g., https://www.youtube.com/@username or https://www.youtube.com/playlist?list=...)"
                      value={bulkImportUrl}
                      onChange={(e) => setBulkImportUrl(e.target.value)}
                      className="flex-1 px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={fetchBulkVideos}
                      disabled={!bulkImportUrl.trim() || isLoadingBulk}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
                    >
                      {isLoadingBulk ? 'Loading...' : 'Fetch Videos'}
                    </button>
                  </div>

                  {fetchedVideos.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm">
                          <p className="font-medium text-gray-700">
                            {bulkScanSummary || `Found ${fetchedVideos.length} videos`}
                          </p>
                          <p className="text-slate-500">
                            Only the new ones are ticked. Videos already in the database are
                            greyed out.
                          </p>
                          {/* A channel posts far more than it collaborates on,
                              so most of a scan is solo videos this app has no
                              reason to hold. Matching on a creator you already
                              track is the closest usable test for whether a
                              video is a collaboration at all. */}
                          <label className="mt-1 inline-flex items-center gap-2 text-slate-700">
                            <input
                              type="checkbox"
                              checked={onlyLikelyCollabs}
                              onChange={(e) => setOnlyLikelyCollabs(e.target.checked)}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            Only show videos that mention a creator you already track
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              setSelectedVideos(
                                new Set(fetchedVideos.filter((v) => !v.alreadyHave).map((v) => v.id))
                              )
                            }
                            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                          >
                            Select new
                          </button>
                          <button
                            onClick={() => setSelectedVideos(new Set(fetchedVideos.map(v => v.id)))}
                            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                          >
                            Select All
                          </button>
                          <button
                            onClick={() => setSelectedVideos(new Set())}
                            className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>

                      {/* A scan of every channel returns hundreds of videos
                          from a dozen sources, and importing "all of Nikki
                          Howard but none of Perfect Person" is the obvious
                          thing to want. One row per channel, each able to
                          select or clear its own videos, and collapsible so a
                          channel you have dealt with gets out of the way. */}
                      {videosByChannel.length > 1 && (
                        <div className="mb-3 rounded-lg ring-1 ring-slate-200 overflow-hidden">
                          {videosByChannel.map(({ channel, items }) => {
                            const ids = items.map((v) => v.id);
                            const chosen = ids.filter((id) => selectedVideos.has(id)).length;
                            const collapsed = collapsedChannels.has(channel);
                            return (
                              <div
                                key={channel}
                                className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 text-sm"
                              >
                                <button
                                  onClick={() =>
                                    setCollapsedChannels((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(channel)) next.delete(channel);
                                      else next.add(channel);
                                      return next;
                                    })
                                  }
                                  className="text-slate-400 hover:text-slate-700"
                                  aria-label={collapsed ? 'Show these' : 'Hide these'}
                                >
                                  {collapsed ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronUp className="w-4 h-4" />
                                  )}
                                </button>
                                <span className="flex-1 font-medium text-slate-800 truncate">
                                  {channel || '(unknown channel)'}
                                </span>
                                <span className="text-slate-500 whitespace-nowrap">
                                  {chosen} of {items.length} selected
                                </span>
                                <button
                                  onClick={() =>
                                    setSelectedVideos((prev) => {
                                      const next = new Set(prev);
                                      ids.forEach((id) => next.add(id));
                                      return next;
                                    })
                                  }
                                  className="text-purple-700 hover:text-purple-900 whitespace-nowrap"
                                >
                                  all
                                </button>
                                <button
                                  onClick={() =>
                                    setSelectedVideos((prev) => {
                                      const next = new Set(prev);
                                      ids.forEach((id) => next.delete(id));
                                      return next;
                                    })
                                  }
                                  className="text-slate-500 hover:text-slate-800 whitespace-nowrap"
                                >
                                  none
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {visibleFetchedVideos
                          .filter((v) => !collapsedChannels.has(v.channelTitle || ''))
                          .map((video) => (
                          <div
                            key={video.id}
                            className={`rounded-lg p-3 flex items-center gap-3 transition ring-1 ${
                              video.alreadyHave
                                ? 'bg-slate-50 ring-slate-200 opacity-60'
                                : 'bg-white ring-slate-200 hover:ring-purple-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedVideos.has(video.id)}
                              onChange={(e) => {
                                const newSelected = new Set(selectedVideos);
                                if (e.target.checked) {
                                  newSelected.add(video.id);
                                } else {
                                  newSelected.delete(video.id);
                                }
                                setSelectedVideos(newSelected);
                              }}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              className="w-24 h-18 object-cover rounded"
                            />
                            <div className="flex-1">
                              <h4 className="font-medium text-sm text-gray-900 line-clamp-2">{video.title}</h4>
                              <p className="text-xs text-gray-500 mt-1">
                                {video.channelTitle} • {video.publishedAt}
                                {video.alreadyHave && (
                                  <span className="ml-2 text-slate-500">already in the database</span>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {selectedVideos.size > 0 && (
                        <div className="mt-4 bg-purple-100 rounded-lg p-4">
                          {bulkImportProgress.total > 0 ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm font-medium text-purple-900">
                                <span>Importing videos...</span>
                                <span>{bulkImportProgress.current} / {bulkImportProgress.total}</span>
                              </div>
                              <div className="w-full bg-purple-200 rounded-full h-2">
                                <div
                                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${(bulkImportProgress.current / bulkImportProgress.total) * 100}%` }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-purple-900">
                                {selectedVideos.size} video{selectedVideos.size !== 1 ? 's' : ''} selected
                              </p>
                              <button
                                onClick={importSelectedVideos}
                                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                              >
                                <Plus className="w-5 h-5" />
                                Import Selected Videos
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Rescan Collaborators Section */}
                <div className="bg-white ring-1 ring-slate-200 rounded-lg p-6 border-2 border-green-200">
                  <h2 className="text-xl font-semibold mb-4 text-green-900 flex items-center gap-2">
                    <Users className="w-6 h-6" />
                    Rescan for Collaborators
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Re-analyze existing videos to detect collaborators that may have been missed. Select videos below and click "Rescan Selected" to add any newly detected collaborators.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedRescanVideos(new Set(videos.map(v => v.id)))}
                          className="text-sm text-green-600 hover:text-green-800 font-medium px-3 py-1 bg-white rounded border border-green-300"
                        >
                          Select All Videos
                        </button>
                        <button
                          onClick={() => setSelectedRescanVideos(new Set())}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1 bg-white rounded border border-gray-300"
                        >
                          Deselect All
                        </button>
                      </div>
                      {selectedRescanVideos.size > 0 && (
                        <p className="text-sm font-medium text-green-900">
                          {selectedRescanVideos.size} video{selectedRescanVideos.size !== 1 ? 's' : ''} selected
                        </p>
                      )}
                    </div>

                    <div className="max-h-96 overflow-y-auto space-y-2 bg-white rounded-lg p-4 border border-green-200">
                      {videos.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No videos to rescan</p>
                      ) : (
                        videos.map((video) => (
                          <div
                            key={video.id}
                            className="flex items-center gap-3 p-2 hover:bg-green-50 rounded transition"
                          >
                            <input
                              type="checkbox"
                              checked={selectedRescanVideos.has(video.id)}
                              onChange={(e) => {
                                const newSelected = new Set(selectedRescanVideos);
                                if (e.target.checked) {
                                  newSelected.add(video.id);
                                } else {
                                  newSelected.delete(video.id);
                                }
                                setSelectedRescanVideos(newSelected);
                              }}
                              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                            />
                            <div className="flex-1">
                              <h4 className="font-medium text-sm text-gray-900">{video.title}</h4>
                              <p className="text-xs text-gray-500 mt-1">
                                Current collaborators: {video.authors && video.authors.length > 0 ? video.authors.map(a => a.name).join(', ') : 'None'}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {selectedRescanVideos.size > 0 && (
                      <div className="bg-green-100 rounded-lg p-4">
                        {isRescanning ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm font-medium text-green-900">
                              <span>Rescanning videos for collaborators...</span>
                              <span>{rescanProgress.current} / {rescanProgress.total}</span>
                            </div>
                            <div className="w-full bg-green-200 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${rescanProgress.total > 0 ? (rescanProgress.current / rescanProgress.total) * 100 : 0}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-700">
                              Ready to rescan {selectedRescanVideos.size} video{selectedRescanVideos.size !== 1 ? 's' : ''}
                            </p>
                            <button
                              onClick={rescanVideoCollaborators}
                              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                            >
                              <Users className="w-5 h-5" />
                              Rescan Selected
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <h2 className="text-xl font-semibold mb-4 text-blue-900">Add New Video</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Video title"
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        className="px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                     <div className="flex gap-2">
  <input
    type="text"
    placeholder="Video URL (YouTube)"
    value={newVideo.url}
    onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })}
    className="flex-1 px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <button
    onClick={fetchVideoInfo}
    disabled={!newVideo.url.trim()}
    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
    title="Auto-fill video info from URL"
  >
    🔍 Fetch Info
  </button>
</div>
                      <input
                        type="date"
                        value={newVideo.date}
                        onChange={(e) => setNewVideo({ ...newVideo, date: e.target.value })}
                        className="px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Creators (at least one required)
                      </label>
                      <div className="mb-3">
                        <CreatorPicker
                          authors={authors}
                          selectedIds={newVideo.authorIds}
                          onChange={(ids) => setNewVideo({ ...newVideo, authorIds: ids })}
                          emptyHint="Use the button below to create them."
                        />
                      </div>

                      {!showAddCreatorInline && (
                        <button
                          type="button"
                          onClick={() => setShowAddCreatorInline(true)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 mb-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add new creator
                        </button>
                      )}
                      
                      {showAddCreatorInline && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-2">
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Quick Add Creator
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Creator name"
                              value={newCreatorName}
                              onChange={(e) => setNewCreatorName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && addCreatorInline()}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <button
                              type="button"
                              onClick={addCreatorInline}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddCreatorInline(false);
                                setNewCreatorName('');
                              }}
                              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pending Collaborator Suggestions */}
                    {(pendingCollaborators.existing.length > 0 || pendingCollaborators.toCreate.length > 0) && (
                      <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold text-green-900 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            Detected Collaborators
                          </h3>
                          <button
                            onClick={clearPendingCollaborators}
                            className="text-sm text-gray-600 hover:text-gray-800"
                          >
                            Clear All
                          </button>
                        </div>

                        {pendingCollaborators.existing.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Existing Creators:</p>
                            <div className="space-y-2">
                              {pendingCollaborators.existing.map(collab => (
                                <div key={collab.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-200">
                                  <span className="font-medium text-gray-900">{collab.name}</span>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => acceptExistingCollaborator(collab)}
                                      className="bg-green-600 text-white px-4 py-1 rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                      Add
                                    </button>
                                    <button
                                      onClick={() => rejectCollaborator(collab)}
                                      className="bg-gray-200 text-gray-700 px-4 py-1 rounded-lg hover:bg-gray-300 transition text-sm flex items-center gap-1"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Skip
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {pendingCollaborators.toCreate.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">New Creators (will be created):</p>
                            <div className="space-y-2">
                              {pendingCollaborators.toCreate.map((collab, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                                  <div>
                                    <span className="font-medium text-gray-900">{collab.name}</span>
                                    <span className="text-xs text-gray-600 ml-2">(new)</span>
                                    {collab.url && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {collab.url.startsWith('http') ? (
                                          <>{detectPlatform(collab.url)}: {collab.url.length > 40 ? collab.url.substring(0, 40) + '...' : collab.url}</>
                                        ) : (
                                          <>Instagram: @{collab.url}</>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => acceptNewCollaborator(collab)}
                                      className="bg-green-600 text-white px-4 py-1 rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                      Create & Add
                                    </button>
                                    <button
                                      onClick={() => rejectCollaborator(collab)}
                                      className="bg-gray-200 text-gray-700 px-4 py-1 rounded-lg hover:bg-gray-300 transition text-sm flex items-center gap-1"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Skip
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={addVideo}
                      disabled={!newVideo.title.trim() || newVideo.authorIds.length === 0}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-5 h-5" />
                      Add Video
                    </button>
                  </div>
                </div>

                {/* Bulk Delete Videos Section */}
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-red-900">Bulk Delete Videos</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedVideoIds(new Set(videos.map(v => v.id)))}
                        className="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1 bg-white rounded border border-red-300"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedVideoIds(new Set())}
                        className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1 bg-white rounded border border-gray-300"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>

                  {selectedVideoIds.size > 0 && (
                    <div className="bg-red-100 rounded-lg p-3 flex items-center justify-between">
                      <p className="text-sm font-medium text-red-900">
                        {selectedVideoIds.size} video{selectedVideoIds.size !== 1 ? 's' : ''} selected
                      </p>
                      <button
                        onClick={bulkDeleteVideos}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Selected
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="text-xl font-semibold mb-4">All Videos ({videos.length})</h2>
                  <div className="space-y-4">
                    {videos.map(video => (
                      <div key={video.id}>
                        {editingVideo?.id === video.id ? (
                          <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4">
                            <h3 className="font-semibold text-sm mb-3 text-blue-900">Editing Video</h3>
                            <div className="space-y-3">
                              <input
                                type="text"
                                placeholder="Video title"
                                value={editingVideo.title}
                                onChange={(e) => setEditingVideo({ ...editingVideo, title: e.target.value })}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <input
                                type="text"
                                placeholder="Video URL"
                                value={editingVideo.url}
                                onChange={(e) => setEditingVideo({ ...editingVideo, url: e.target.value })}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <input
                                type="date"
                                value={editingVideo.date}
                                onChange={(e) => setEditingVideo({ ...editingVideo, date: e.target.value })}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">
                                  Select Creators
                                </label>
                                <CreatorPicker
                                  authors={authors}
                                  selectedIds={editingVideo.authorIds}
                                  onChange={(ids) =>
                                    setEditingVideo({ ...editingVideo, authorIds: ids })
                                  }
                                />
                              </div>
                              <div className="flex gap-2 pt-2">
                                <button
                                  onClick={updateVideo}
                                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                                >
                                  Save Changes
                                </button>
                                <button
                                  onClick={cancelEditingVideo}
                                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selectedVideoIds.has(video.id)}
                                onChange={(e) => {
                                  const newSelected = new Set(selectedVideoIds);
                                  if (e.target.checked) {
                                    newSelected.add(video.id);
                                  } else {
                                    newSelected.delete(video.id);
                                  }
                                  setSelectedVideoIds(newSelected);
                                }}
                                className="w-5 h-5 text-red-600 rounded focus:ring-red-500 mt-1"
                              />
                              <div className="flex-1 flex items-start justify-between gap-4">
                              <a
                                href={video.url || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <VideoThumb url={video.url} title={video.title} size="sm" />
                              </a>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-base mb-2 text-slate-900">{video.title}</h3>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {video.authorIds.map(authorId => (
                                    <span
                                      key={authorId}
                                      className={`px-3 py-1 rounded-full text-sm ${
                                        video.authorIds.length > 1
                                          ? 'bg-purple-100 text-purple-700'
                                          : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {getAuthorName(authorId)}
                                    </span>
                                  ))}
                                  {video.authorIds.length > 1 && (
                                    <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700 font-medium">
                                      Collaboration
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  {video.date && <span>{video.date}</span>}
                                  {video.url && (
                                    <a
                                      href={video.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                    >
                                      Watch <ExternalLink className="w-4 h-4" />
                                    </a>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => rescanSingleVideo(video)}
                                  className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded"
                                  title="Rescan for collaborators"
                                >
                                  <RefreshCw className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => startEditingVideo(video)}
                                  className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                                  title="Edit video"
                                >
                                  <Edit className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => deleteVideo(video.id)}
                                  className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded"
                                  title="Delete video"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'stats' && isAdmin && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-600 rounded-lg p-6 text-white">
                    <div className="text-3xl font-bold">{authors.length}</div>
                    <div className="text-orange-100">Total Creators</div>
                  </div>
                  <div className="bg-amber-500 rounded-lg p-6 text-white">
                    <div className="text-3xl font-bold">{videos.length}</div>
                    <div className="text-amber-100">Total Videos</div>
                  </div>
                  <div className="bg-emerald-600 rounded-lg p-6 text-white">
                    <div className="text-3xl font-bold">
                      {videos.filter(v => v.authorIds.length > 1).length}
                    </div>
                    <div className="text-green-100">Collaborations</div>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl font-semibold mb-4">Creator Statistics</h2>
                  <div className="space-y-4">
                    {getCollaborationStats().map(stat => (
                      <div key={stat.author.id} className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">{stat.author.name}</h3>
                          <button
                            onClick={() => viewAuthorPage(stat.author)}
                            className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1"
                          >
                            View Profile <ArrowLeft className="w-4 h-4 transform rotate-180" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">{stat.totalVideos}</div>
                            <div className="text-sm text-gray-600">Total Videos</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-600">{stat.soloVideos}</div>
                            <div className="text-sm text-gray-600">Solo Videos</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-amber-600">{stat.collabVideos}</div>
                            <div className="text-sm text-gray-600">Collaborations</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{stat.uniqueCollaborators}</div>
                            <div className="text-sm text-gray-600">Unique Collaborators</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with version */}
        <div className="mt-auto pt-4 pb-2 text-center text-sm text-gray-500">
          <p>Friends of the Channel v{appVersion}</p>
        </div>
      </div>
    </div>
  );
}